/* eslint-disable react-hooks/exhaustive-deps */
import React, { useRef, useState, useEffect } from 'react';
import { makeStyles, Theme, createStyles, Tooltip, withStyles, Paper, Button, IconButton, Chip } from '@material-ui/core';
import clsx from 'clsx';
import FileCopyOutlinedIcon from '@material-ui/icons/FileCopyOutlined';
import CheckIcon from '@material-ui/icons/Check';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import HistoryOutlinedIcon from '@material-ui/icons/HistoryOutlined';
import moment from 'moment';
import { mainTheme } from '../../Theme';
import { TaskDetails, TaskSummaryList } from '../../leadsDetail/LeadsDetailRepository';
import { EnquiryDetail } from '../models/Enquiry';
import { AnimatedItem } from '../../components/animations/AnimatedItem';
import CheckOutlinedIcon from '@material-ui/icons/CheckOutlined';
import RestoreIcon from '@material-ui/icons/Restore';
import AssignmentIndOutlinedIcon from '@material-ui/icons/AssignmentIndOutlined';
import PeopleOutlineOutlinedIcon from '@material-ui/icons/PeopleOutlineOutlined';
import CallOutlinedIcon from '@material-ui/icons/CallOutlined';
import SmsOutlinedIcon from '@material-ui/icons/SmsOutlined';
import NotesOutlinedIcon from '@material-ui/icons/NotesOutlined';
import MailOutlinedIcon from '@material-ui/icons/MailOutlined';
import GavelIcon from '@material-ui/icons/Gavel';
import DescriptionOutlinedIcon from '@material-ui/icons/DescriptionOutlined';
import PermContactCalendarOutlinedIcon from '@material-ui/icons/PermContactCalendarOutlined';
import NotListedLocationOutlinedIcon from '@material-ui/icons/NotListedLocationOutlined';
import DraftsOutlinedIcon from '@material-ui/icons/DraftsOutlined';
import NotInterestedOutlinedIcon from '@material-ui/icons/NotInterestedOutlined';
import EventOutlinedIcon from '@material-ui/icons/EventOutlined';
import AssignmentOutlinedIcon from '@material-ui/icons/AssignmentOutlined';
import SupervisorAccountOutlinedIcon from '@material-ui/icons/SupervisorAccountOutlined';
import AccessTimeIcon from '@material-ui/icons/AccessTime';
import AutorenewOutlinedIcon from '@material-ui/icons/AutorenewOutlined';
import ReportProblemOutlinedIcon from '@material-ui/icons/ReportProblemOutlined';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
// import { MatterTerminology } from '../../MatterTerminology';
import { formatNumber } from '../../helpers/ChartHelper';
// import { CurrencySymbolData, retrieveCurrencySymbolData } from '../../ClientAndContactDetails/referrals/ReferralsDetailsRepository';
import { EmailContentDialog } from './EmailContentDialog';
import { TimezoneOption, } from '../../lookupConfiguration/selectors/TimezoneSelector';
import LinkOffOutlinedIcon from '@material-ui/icons/LinkOffOutlined';
import LinkOutlinedIcon from '@material-ui/icons/LinkOutlined';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import RemoveCircleOutlinedIcon from '@material-ui/icons/RemoveCircleOutlined';
import { useTranslation } from 'react-i18next';
import { TranslationTextList } from '../../locales/common';
import { isBookMeetingDone as isBookMeetingDoneIn } from './historyActivityRules';
// DEBUG: render-count chips, only when the flag is on (see helpers/renderCountDebug)
import { IS_RENDER_COUNT_ENABLED, RenderCountBadge } from '../../helpers/renderCountDebug';

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            // Explicit bug report: the sticky filterRow (top: 0, below) stuck with a visible
            // gap above it and let scrolled content show through - root cause was this
            // padding-top sitting inside the same scrolling box filterRow sticks within, which
            // sticky positioning doesn't exclude itself from. No top padding needed since
            // filterRow's own padding (10px 0 14px) already gives it breathing room; bottom
            // padding stays for spacing after the last timeline item.
            paddingTop: 0,
            paddingBottom: '20px',
            flex: 1,
            display: 'flex',
            flexFlow: 'column',
            overflowY: 'auto',
            marginBottom: '10px',
            // maxWidth: '500px',
            position: 'relative',
            // Explicit bug report: the rail line (historyContainer's/dayHeaderRow's own
            // '.connector', each spanning its own row's full top:0-to-bottom:0) dangled past
            // the very last icon, with nothing below it to connect to. Each row's connector
            // only needs to draw UP INTO its own icon - the previous row's connector already
            // reaches down to this row's top edge - so removing just the LAST row's own
            // connector stops the line there instead of carrying on through its padding-bottom.
            // :nth-last-child(2) (not :last-child) because the trailing <div ref={messagesEndRef}
            // /> is always the true last child - the row before it is what we want.
            '& > *:nth-last-child(2) .connector': {
                display: 'none',
            },
        },
        // Explicit request: filter the timeline by activity type. A plain row of toggleable
        // MUI Chips above the feed - client-side only (leadDetailsHistory is already fully
        // loaded by the time this renders), so no new query is needed.
        // Explicit request: keep this row visible while scrolling the feed beneath it - sticky
        // relative to classes.root (the scrolling ancestor, overflowY:'auto' above), with a
        // solid background so scrolled-past items don't show through underneath it.
        filterRow: {
            display: 'flex',
            flexWrap: 'wrap',
            gap: theme.spacing(1),
            padding: '10px 0 14px',
            position: 'sticky',
            top: 0,
            zIndex: 2,
            backgroundColor: '#fff',
        },
        // the chips themselves, as wide as they are (filterRow is the full-width sticky bar behind them): what the enquiry form tour points at
        filterRowContent: {
            display: 'flex',
            flexWrap: 'wrap',
            gap: theme.spacing(1),
            width: 'fit-content',
            maxWidth: '100%',
        },
        filterChip: {
            fontSize: 12.5,
        },
        loadingRoot: {
            padding: '20px 0',
            flex: 1,
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'center',
            position: 'relative',
            marginBottom: '10px',
        },
        // ── Timeline redesign (explicit request - UI only, same data/logic every branch
        // below already had) ──────────────────────────────────────────────────────────
        // Two columns per row: a 32px rail column (railNode), then the card/line content,
        // 14px gap. padding-bottom (not margin) on each row keeps rows flush against each
        // other so the per-row connector line reads as one continuous rail rather than the
        // old broken-looking per-icon segments.
        historyContainer: {
            display: 'flex',
            alignItems: 'flex-start',
            width: '100%',
            paddingBottom: 14,
            gap: 14,
            position: 'relative',
            '& .connector': {
                borderLeft: '2px solid #E2E7EE',
                position: 'absolute',
                top: 0,
                bottom: 0,
                left: 15,
                zIndex: 0,
            }
        },
        // Explicit bug report: flex-start (above) keeps the rail icon pinned to the TOP of a
        // (often multi-line) card, which is correct there, but a quiet line is a single short
        // row - top-aligning it against the 32px icon left it sitting visibly above the icon's
        // own centre instead of level with it.
        historyContainerCentered: {
            alignItems: 'center',
        },
        historyIcon: {
            display: 'flex',
            flexShrink: 0,
        },
        // 32px node, centred on the rail. box-shadow punches a 4px ring of the panel's own
        // background colour around it so the connector line behind appears to pass under it
        // rather than through it - same idea the old historyIconChip comment already
        // explained for the old smaller chip, just sized to the brief's 32px node.
        historyIconChip: {
            position: 'relative',
            zIndex: 1,
            width: 32,
            height: 32,
            minWidth: 32,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            boxShadow: '0 0 0 4px #fff',
        },
        // Explicit request: a day header row before each new calendar day - a small hollow
        // dot on the rail (same 32px column so it lines up with the real nodes) plus an
        // uppercase label. "Today" gets its own primary-coloured word.
        dayHeaderRow: {
            display: 'flex',
            alignItems: 'center',
            gap: 14,
            paddingBottom: 10,
            position: 'relative',
            // Same rail line as historyContainer's own '.connector', duplicated here so the
            // rail reads as one continuous line through a day header instead of breaking at
            // every day boundary.
            '& .connector': {
                borderLeft: '2px solid #E2E7EE',
                position: 'absolute',
                top: 0,
                bottom: 0,
                left: 15,
                zIndex: 0,
            }
        },
        dayHeaderDotWrapper: {
            display: 'flex',
            justifyContent: 'center',
            width: 32,
            flexShrink: 0,
        },
        dayHeaderDot: {
            // Needs position+zIndex (same as historyIconChip) - a static box paints BEHIND a
            // positioned one by default, so without this the connector's zIndex:0 line
            // painted over the dot instead of being punched through by its boxShadow ring.
            position: 'relative',
            zIndex: 1,
            width: 10,
            height: 10,
            borderRadius: '50%',
            border: '2px solid #CDD4DF',
            backgroundColor: '#fff',
            boxShadow: '0 0 0 4px #fff',
        },
        dayHeaderLabel: {
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            color: mainTheme.TextColor.Secondary,
            '& .today': {
                color: mainTheme.TemplateColor.Primary,
            },
        },
        // Explicit bug report: calls/notes with no body text rendered as a card containing
        // only the author's name. Those (and other no-content system events - status/owner
        // changes etc.) now render as a single quiet line instead of an empty-looking card -
        // same historyContainer/historyIcon rail column as a real card (below), just this
        // plain inline row instead of activityCard for the content side, so there's only one
        // rail-node size/treatment in the whole timeline rather than two.
        quietEventText: {
            flex: 1,
            minWidth: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 8,
            fontSize: 13,
            color: mainTheme.TextColor.Secondary,
            // Explicit bug report: this row has no card around it to inset its own right edge,
            // so its time sat flush against the timeline's outer edge - 12px further right than
            // a card's own time, which is inset by cardHeaderRow's own 12px right padding.
            // Matching that here right-aligns both to the same edge.
            paddingRight: 12,
            '& strong': {
                fontWeight: 500,
                color: mainTheme.TextColor.Primary,
            },
        },
        // Explicit request: the assignment quiet line gets the same dashed-card treatment as
        // the upcoming Follow Up Due card (activityCardUpcoming) - a dashed border + tinted
        // background around the whole row. Explicit correction: grey rather than green - an
        // assignment isn't an "outcome reached" event, so it shouldn't borrow that colour's
        // meaning. Own padding (shorthand) intentionally supersedes quietEventText's own
        // paddingRight above once combined via clsx, since this box insets its own right edge
        // the same way a real card would.
        quietEventAssigned: {
            border: '1px dashed rgba(0,0,0,0.16)',
            backgroundColor: 'rgba(0,0,0,0.035)',
            borderRadius: 8,
            padding: '8px 12px',
        },
        quietEventTime: {
            flexShrink: 0,
            fontSize: 12,
            color: mainTheme.TextColor.Secondary,
            opacity: 0.8,
            fontVariantNumeric: 'tabular-nums',
        },
        // Same indigo pill as '.title .assigneeChip'/'.description .assigneeChip' in
        // contentDetails below, as a standalone class - the quiet line (e.g. assignment's
        // "X assigned this Enquiry to [chip]") sits outside contentDetails, so those nested
        // selectors don't reach it.
        inlineChip: {
            display: 'inline-flex',
            alignItems: 'center',
            padding: '1px 8px',
            borderRadius: 999,
            fontWeight: 700,
            fontSize: '12.5px',
            backgroundColor: 'var(--rv-accent-indigo-bg)',
            color: 'var(--rv-accent-indigo)',
        },
        // Card header row - type label, optional secondary descriptor, avatar+author,
        // right-aligned time, hover-only copy button (see cardCopyBtn).
        cardHeaderRow: {
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 8,
            minHeight: 42,
            padding: '9px 12px 9px 14px',
        },
        cardHeaderRowWithBody: {
            borderBottom: '1px solid #E2E7EE',
        },
        cardTypeLabel: {
            fontSize: 13,
            fontWeight: 700,
        },
        cardAuthor: {
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            minWidth: 0,
        },
        cardAuthorByLabel: {
            fontSize: 13,
            color: mainTheme.TextColor.Secondary,
            flexShrink: 0,
        },
        cardAuthorAvatar: {
            width: 20,
            height: 20,
            minWidth: 20,
            borderRadius: '50%',
            backgroundColor: '#EEF1F5',
            color: mainTheme.TextColor.Secondary,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 9.5,
            fontWeight: 700,
            flexShrink: 0,
        },
        cardAuthorName: {
            fontSize: 13,
            color: mainTheme.TextColor.Secondary,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
        },
        cardHeaderRight: {
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            marginLeft: 'auto',
        },
        cardCopyBtn: {
            width: 26,
            height: 26,
            padding: 0,
            opacity: 0,
            color: theme.palette.text.disabled,
            transition: 'opacity 0.15s ease, color 0.15s ease',
            '&:hover': {
                color: mainTheme.BrandColors.TemplatePrimary,
            },
            '&:focus': {
                opacity: 1,
            },
            '& svg': {
                fontSize: 15,
            },
        },
        cardTime: {
            fontSize: 12,
            color: mainTheme.TextColor.Secondary,
            opacity: 0.8,
            fontVariantNumeric: 'tabular-nums',
            whiteSpace: 'nowrap',
        },
        // Explicit request: this is now the card BODY (padding 12px 14px 14px, 14px/1.55) -
        // the old icon+narrow-label-column+content flex row is gone, see historyContainer
        // above. '.title'/'.description'/'.assigneeChip'/'.mentioned'/'.wordBreak' are still
        // referenced by plain className string throughout getDetailContent's branches below
        // (unchanged - same data, same conditions, every field still shown), just restyled.
        contentDetails: {
            padding: '12px 14px 14px',
            fontSize: 14,
            lineHeight: 1.55,
            '& .title': {
                fontSize: 14,
                fontWeight: 400,
                color: mainTheme.TextColor.Secondary,
                marginBottom: 5,
                '& span': {
                    fontWeight: 700,
                    color: mainTheme.TextColor.Primary,
                },
                // Explicit request: render the assignee's own name as a small pill rather than
                // plain bold text - tied to the same indigo accent this card's own timeline icon
                // already uses (getHistoryAvatar's "assign enquiry" branch), so the chip visually
                // traces back to its own icon instead of picking an arbitrary new colour. Nested
                // here (not a standalone top-level class) so '.title .assigneeChip' outweighs the
                // plain '.title span' rule above it.
                '& .assigneeChip': {
                    display: 'inline-flex',
                    alignItems: 'center',
                    padding: '1px 8px',
                    borderRadius: 999,
                    fontWeight: 700,
                    fontSize: '12.5px',
                    backgroundColor: 'var(--rv-accent-indigo-bg)',
                    color: 'var(--rv-accent-indigo)',
                },
            },
            '& .description': {
                marginTop: 10,
                fontSize: 14,
                lineHeight: 1.55,
                color: mainTheme.TextColor.Secondary,
                '& span': {
                    fontWeight: 700,
                    color: mainTheme.TextColor.Primary,
                },
                // Same chip as '.title .assigneeChip' above (see that rule's own comment) -
                // explicit request to give "Meeting with X" (getAssignedTo's own sentence) the
                // same treatment as "Assigned to X". Duplicated rather than shared because a
                // standalone top-level class would lose specificity to '.description span'
                // above it - nesting here keeps '.description .assigneeChip' one class deeper.
                // .mentioned is this same file's own convertMentionInCommentToHtml output (the
                // literal HTML string a Follow Up/Internal Note's dangerouslySetInnerHTML
                // renders for an @-mention, e.g. "@[Commercial|Team](user:...)") - explicit
                // request to give a mentioned team/staff/fee earner the same chip, since a note
                // can mention several different people/teams inline, not just one assignee.
                '& .assigneeChip, & .mentioned': {
                    display: 'inline-flex',
                    alignItems: 'center',
                    padding: '1px 8px',
                    borderRadius: 999,
                    fontWeight: 700,
                    fontSize: '12.5px',
                    backgroundColor: 'var(--rv-accent-indigo-bg)',
                    color: 'var(--rv-accent-indigo)',
                },
            },
            '& .wordBreak': {
                'word-break': 'break-all'
            },
            // Explicit request: the author is already shown in the new card header (avatar +
            // name) - every '.author' DIV in getDetailContent below is the same
            // task.assignedByName value repeated in the body ("Created by:"/"Sent by:" etc.),
            // so hiding this via CSS removes the duplicate without touching any of those 17
            // branches' JSX/logic. The information isn't lost, just no longer shown twice.
            // Explicit bug report: scoped to 'div.author' specifically (not the bare '.author'
            // class this used to be) - getAutFollowUp/getAutoFollowUpRemoved also reuse the
            // SAME "author" className on their own, unrelated <p> elements ("Auto Follow Up:
            // {template}", "Auto Follow Up Removed"), which the broader selector was hiding
            // too, leaving some cards (whose only real content was one of those two messages)
            // looking completely empty.
            '& div.author': {
                display: 'none',
            },
            // Same reasoning as '.author' above, for the branches that show the name as a
            // bare h5 title (task.assignedByName/task.ownerName alone, no other text) instead
            // of a "Created by:"/"Sent by:" line - still the same value the header's "by
            // {author}" now carries, so hide it here too rather than show it twice.
            '& .authorNameTitle': {
                display: 'none',
            },
            // The legacy Semantic UI stylesheet gives every heading that is not :first-child a ~28px top margin. The hidden
            // name heading above is still the first child, so the heading after it (the Link Matter title) got that margin
            // and showed as a blank band under the card header.
            '& .authorNameTitle + .title': {
                marginTop: 0,
            },
        },
        chips: {
            display: 'flex',
            flexWrap: 'wrap',
        },
        chip: {
            marginLeft: '8px'
        },
        // Enquiry Created card: area of law + source used to be one bold "Criminal (Email)" heading, which
        // read as a title rather than two facts - each is now an outlined chip with an icon and a muted
        // label so they are obviously "what it's about" and "how it came in". The area-of-law icon is
        // the same GavelIcon the enquiry form's header badge uses.
        enquiryCreatedMeta: {
            display: 'flex',
            flexWrap: 'wrap',
            gap: 8,
            marginBottom: 12,
        },
        enquiryCreatedMetaItem: {
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            padding: '3px 10px 3px 8px',
            border: '1px solid #E2E7EE',
            borderRadius: 999,
            backgroundColor: '#fff',
            fontSize: '13.5px',
            lineHeight: 1.4,
        },
        enquiryCreatedMetaIconArea: {
            fontSize: 16,
            color: 'var(--rv-accent-blue)',
        },
        enquiryCreatedMetaIconSource: {
            fontSize: 16,
            color: 'var(--rv-accent-teal)',
        },
        enquiryCreatedMetaLabel: {
            color: '#6b7280',
        },
        enquiryCreatedMetaValue: {
            fontWeight: 700,
        },
        // Explicit request: the card's own border/shadow/hover now lives on activityCard
        // (wraps this + the new header row - see activityContent/enquiryCreated/
        // notProceeding below), not here - this stays only because 17 getDetailContent
        // branches already reference it as their <Paper> wrapper, and swapping all 17 to a
        // plain <div> is a bigger/riskier diff than just neutralising this one's own
        // elevation via CSS. !important beats MuiPaper-elevation3's own generated class
        // regardless of which one JSS happens to insert last (same pattern already used
        // elsewhere in this app for exactly this kind of two-class conflict).
        detailsContainer: {
            padding: 0,
            width: '100%',
            position: 'relative',
            backgroundColor: 'transparent',
            boxShadow: 'none !important',
            '& .due-date': {
                color: '#fcba03'
            },
        },
        // The outer card shell - wraps cardHeaderRow + contentDetails. Explicit request:
        // border: 1px solid #E2E7EE; border-radius: 10px; subtle shadow; hover lifts with a
        // stronger shadow, respecting prefers-reduced-motion. No coloured left border/accent
        // bar - colour lives in the rail node and the type label only.
        activityCard: {
            flex: 1,
            minWidth: 0,
            backgroundColor: '#fff',
            border: '1px solid #E2E7EE',
            // Explicit correction: 4px, matching the app-wide panel-container radius (TaskFlow/
            // marketing automation's own dialogs/panels, ConfigureTaskDialog.tsx) rather than
            // this card's own earlier 10px - 10px is reserved for smaller inner elements (chips,
            // icon badges), not panel-level containers like this one.
            borderRadius: 4,
            boxShadow: '0 1px 2px rgba(20,30,50,.05)',
            transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
            '&:hover': {
                borderColor: '#CDD4DF',
                boxShadow: '0 8px 18px -10px rgba(20,30,50,.28)',
                // Explicit request: the header's copy button only shows on hover over the
                // whole card, not just the button itself.
                '& $cardCopyBtn': {
                    opacity: 1,
                },
            },
            '@media (prefers-reduced-motion: reduce)': {
                '&:hover': {
                    boxShadow: '0 1px 2px rgba(20,30,50,.05)',
                },
            },
        },
        // amber-tinted variant for the upcoming Follow Up Due card - dashed border, no
        // shadow, matching the brief's "upcoming item" treatment but using this app's own
        // lighter follow-up orange (#ff964f, already introduced for the enquiry header's
        // Journey widget) rather than a new amber.
        activityCardUpcoming: {
            backgroundColor: 'rgba(255,150,79,0.08)',
            borderStyle: 'dashed',
            borderColor: 'rgba(255,150,79,0.55)',
            boxShadow: 'none',
            '&:hover': {
                borderColor: 'rgba(255,150,79,0.8)',
                boxShadow: 'none',
            },
        },
        // Explicit request: a pill in place of the usual clock time on the upcoming Follow Up
        // Due card, e.g. "in 23 minutes" - same JOURNEY_FOLLOW_UP_COLOR-adjacent orange as
        // activityCardUpcoming above.
        upcomingPill: {
            display: 'inline-flex',
            alignItems: 'center',
            padding: '2px 9px',
            borderRadius: 999,
            fontSize: 11.5,
            fontWeight: 700,
            color: '#9A5200',
            backgroundColor: 'rgba(255,150,79,0.18)',
            whiteSpace: 'nowrap',
        },
        // Explicit request: the Create Matter/Client "didn't finish" card (see isFailedIntegrationTask) reads as
        // an error, not an upcoming item - a solid red-tinted border (the dashed orange activityCardUpcoming
        // above stays reserved for things still to come) with the red warning node and type label.
        activityCardFailed: {
            borderColor: 'rgba(196,49,75,0.25)',
            '&:hover': {
                borderColor: 'rgba(196,49,75,0.45)',
            },
        },
        failedSummary: {
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '4px 12px',
        },
        failedSummaryText: {
            margin: 0,
            fontSize: 14,
            fontWeight: 700,
            color: mainTheme.TextColor.Primary,
        },
        failedProgress: {
            display: 'flex',
            gap: 3,
        },
        failedProgressSegment: {
            display: 'block',
            width: 22,
            height: 6,
            borderRadius: 999,
            backgroundColor: '#EEF0F6',
        },
        failedProgressSegmentDone: {
            backgroundColor: 'var(--rv-accent-green)',
        },
        failedProgressCount: {
            fontSize: 12.5,
            color: mainTheme.TextColor.Secondary,
        },
        // Each step: icon on the left, its name beside it, a status word on the right - not an icon pushed to the far
        // edge, and never colour alone.
        failedStepList: {
            listStyle: 'none',
            margin: '12px 0 0',
            padding: 0,
            maxWidth: 440,
        },
        failedStep: {
            display: 'grid',
            gridTemplateColumns: '22px minmax(0, 1fr) auto',
            alignItems: 'center',
            columnGap: 10,
            padding: '7px 0',
            borderTop: '1px solid #E2E7EE',
            fontSize: 13.5,
            '&:first-child': {
                borderTop: 'none',
            },
        },
        failedStepIcon: {
            fontSize: 20,
        },
        failedStepName: {
            fontWeight: 700,
            color: mainTheme.TextColor.Primary,
        },
        failedStepStatus: {
            fontSize: 12.5,
            fontWeight: 700,
        },
        failedStepDone: {
            '& $failedStepIcon, & $failedStepStatus': {
                color: 'var(--rv-accent-green)',
            },
        },
        failedStepTodo: {
            '& $failedStepIcon': {
                color: '#98A2B3',
            },
            '& $failedStepStatus': {
                color: mainTheme.TextColor.Secondary,
                fontWeight: 600,
            },
        },
        failedGuide: {
            margin: '14px 0 0',
            paddingTop: 12,
            borderTop: '1px solid #E2E7EE',
            '& p': {
                margin: 0,
                fontSize: 13.5,
                color: mainTheme.TextColor.Primary,
            },
            '& p + p': {
                marginTop: 4,
            },
        },
        failedNote: {
            display: 'flex',
            alignItems: 'flex-start',
            gap: 8,
            margin: '12px 0 0',
            padding: '8px 10px',
            borderRadius: 8,
            backgroundColor: '#F7F8FC',
            fontSize: 12.5,
            lineHeight: 1.45,
            color: mainTheme.TextColor.Secondary,
            '& svg': {
                fontSize: 16,
                flexShrink: 0,
                marginTop: 1,
                color: '#98A2B3',
            },
        },
        dialogRoot: {
            '& .downshift-wrapper': {
                flex: 1,
                padding: 0,
            }
        },
        header: {
            textTransform: 'capitalize',
            '& .MuiTypography-root': {
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
            }
        },
        dialogPaper: {
            height: '100%',
        },
        emailContentButton: {
            textAlign: 'end',
            position: 'absolute',
            right: 10,
            top: 10,
        },
        emailDetails: {
            textAlign: 'start',
            paddingRight: 80,
            // Same quiet-label/bold-value idiom as contentDetails's own .title/.description -
            // explicit request to extend it to every "Label: value" row, not just the Assign card.
            '& .emailDescription': {
                color: mainTheme.TextColor.Secondary,
                fontSize: 14,
                '& span': {
                    fontWeight: 700,
                    color: mainTheme.TextColor.Primary,
                },
            }
        },
        // Explicit request (reference: EmailCard.png) - the sent/SMS/data-form cards' own body
        // layout, scoped to the 3 CreateEmail/CreateSMS/SendDataForm branches only (BookMeeting/
        // MeetingReminder above keep their existing emailContentButton/emailDetails treatment,
        // out of scope here): subject as the card's own bold first line, From/To/Cc/Template as
        // a quiet label/value field list, "View full email" replacing the old absolute-positioned
        // "Content" button.
        emailFields: {
            textAlign: 'start',
            // Same rule as emailDetails's own '& .emailDescription' above (duplicated, not
            // shared, for the same specificity reason '.assigneeChip' is duplicated elsewhere
            // in this file) - "Auto Follow Up Sent"/"Previous Notes" still use this class here.
            '& .emailDescription': {
                color: mainTheme.TextColor.Secondary,
                fontSize: 14,
                '& span': {
                    fontWeight: 700,
                    color: mainTheme.TextColor.Primary,
                },
            },
        },
        // Explicit request: the "Auto Follow Up: {template}" line (with its Remove button) and "Auto Follow Up Removed" were plain
        // <p class="author"> and took the legacy .author look (large text in a grey block, the button's own line height). They are now a
        // compact notice row: icon, label with the template name in bold, and Remove at the right edge.
        autoFollowUpNotice: {
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing(1),
            margin: 0,
            padding: theme.spacing(0.5, 1, 0.5, 1.5),
            minHeight: 36,
            borderRadius: 8,
            backgroundColor: 'var(--rv-accent-indigo-bg)',
            color: mainTheme.TextColor.Secondary,
            fontSize: 13,
            lineHeight: 1.4,
        },
        autoFollowUpNoticeRemoved: {
            backgroundColor: theme.palette.grey[100],
        },
        autoFollowUpNoticeIcon: {
            flexShrink: 0,
            fontSize: 18,
            color: 'var(--rv-accent-indigo)',
        },
        autoFollowUpNoticeText: {
            flex: 1,
            minWidth: 0,
        },
        autoFollowUpTemplateName: {
            fontWeight: 700,
            color: mainTheme.TextColor.Primary,
        },
        autoFollowUpRemoveButton: {
            flexShrink: 0,
            minWidth: 0,
            padding: theme.spacing(0.25, 1),
            fontSize: 12.5,
            fontWeight: 600,
            textTransform: 'none',
        },
        emailSubject: {
            fontSize: 14,
            fontWeight: 700,
            color: mainTheme.TextColor.Primary,
            marginBottom: 10,
            lineHeight: 1.4,
        },
        emailFieldRow: {
            display: 'flex',
            gap: 10,
            fontSize: 13,
            lineHeight: 1.9,
        },
        emailFieldLabel: {
            flexShrink: 0,
            width: 66,
            color: mainTheme.TextColor.Secondary,
        },
        emailFieldValue: {
            minWidth: 0,
            color: mainTheme.TextColor.Primary,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
        },
        // Explicit request (option A): From/To/Cc/Template of the sent email / SMS / data form cards sit in one quiet panel under the subject.
        // Addresses wrap as pills instead of being cut off with an ellipsis, and the template is a coloured pill.
        sentDetailsPanel: {
            display: 'grid',
            gridTemplateColumns: 'auto minmax(0, 1fr)',
            columnGap: theme.spacing(1.75),
            rowGap: theme.spacing(0.625),
            alignItems: 'start',
            padding: theme.spacing(1, 1.5),
            borderRadius: 8,
            border: `1px solid ${theme.palette.divider}`,
            backgroundColor: theme.palette.grey[50],
            fontSize: 13,
        },
        sentDetailsLabel: {
            paddingTop: 2,
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            color: mainTheme.TextColor.Secondary,
        },
        sentDetailsValue: {
            minWidth: 0,
            overflowWrap: 'anywhere',
            color: mainTheme.TextColor.Primary,
        },
        sentDetailsPills: {
            display: 'flex',
            flexWrap: 'wrap',
            gap: theme.spacing(0.5, 0.75),
        },
        sentDetailsDivider: {
            gridColumn: '1 / -1',
            margin: theme.spacing(0.25, 0),
            border: 0,
            borderTop: `1px solid ${theme.palette.divider}`,
        },
        sentAddressPill: {
            display: 'inline-block',
            padding: '0 9px',
            borderRadius: 999,
            border: `1px solid ${theme.palette.divider}`,
            backgroundColor: theme.palette.background.paper,
            fontSize: 12.5,
            lineHeight: '22px',
        },
        sentTemplatePill: {
            display: 'inline-flex',
            alignItems: 'center',
            gap: theme.spacing(0.625),
            maxWidth: '100%',
            padding: '0 9px 0 7px',
            borderRadius: 999,
            backgroundColor: 'var(--rv-accent-blue-bg)',
            color: 'var(--rv-accent-blue)',
            fontSize: 12.5,
            fontWeight: 500,
            lineHeight: '22px',
            '& svg': {
                fontSize: 14,
                flexShrink: 0,
            },
            '& span': {
                minWidth: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
            },
        },
        sentTemplatePillSms: {
            backgroundColor: 'var(--rv-accent-purple-bg)',
            color: 'var(--rv-accent-purple)',
        },
        viewEmailLink: {
            marginTop: 10,
            marginLeft: -6,
            padding: '4px 6px',
            minWidth: 'auto',
            fontSize: 13,
            fontWeight: 600,
            textTransform: 'none',
        },
        divider: {
            margin: 10,
            width: '100%',
        },
        notes: {
            backgroundColor: 'rgba(243, 243, 158, 0.29)',
            padding: '10px',
        },
        // Explicit request (reference: MeetingBooked.png) - the Book Meeting card's own body,
        // restyled after that reference's layout: a date badge, "Meeting with [chip]"/date/
        // location block, a Confirmation/Fee/Reminder/Assigned-To stats row, then Meeting Notes.
        // Every field the old single run-on sentence + plain "Label: value" lines showed is
        // still shown here, just laid out - nothing added or dropped.
        meetingBody: {
            display: 'flex',
            gap: 14,
            marginBottom: 14,
        },
        meetingDateBadge: {
            flexShrink: 0,
            width: 56,
            borderRadius: 8,
            overflow: 'hidden',
            border: '1px solid var(--rv-accent-orange)',
            textAlign: 'center',
        },
        meetingDateBadgeMonth: {
            backgroundColor: 'var(--rv-accent-orange)',
            color: '#fff',
            fontSize: 10.5,
            fontWeight: 700,
            letterSpacing: '0.05em',
            textTransform: 'uppercase',
            padding: '3px 0',
        },
        // a booking that is done (see isBookMeetingDone) - same tile, green instead of orange
        meetingDateBadgeDone: {
            borderColor: 'var(--rv-accent-green)',
        },
        meetingDateBadgeMonthDone: {
            backgroundColor: 'var(--rv-accent-green)',
        },
        meetingDateBadgeDay: {
            fontSize: 20,
            fontWeight: 700,
            color: mainTheme.TextColor.Primary,
            padding: '5px 0',
        },
        meetingInfo: {
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: 3,
            paddingTop: 2,
        },
        meetingTitle: {
            fontSize: 14,
            fontWeight: 700,
            color: mainTheme.TextColor.Primary,
            margin: 0,
            // Duplicated from '.title .assigneeChip'/'.description .assigneeChip' (same
            // specificity reasoning those two give) - meetingTitle is its own top-level class.
            '& .assigneeChip': {
                display: 'inline-flex',
                alignItems: 'center',
                padding: '1px 8px',
                borderRadius: 999,
                fontWeight: 700,
                fontSize: '12.5px',
                backgroundColor: 'var(--rv-accent-indigo-bg)',
                color: 'var(--rv-accent-indigo)',
            },
        },
        meetingDateTimeText: {
            fontSize: 13,
            color: mainTheme.TextColor.Primary,
        },
        meetingLocationText: {
            fontSize: 13,
            color: mainTheme.TextColor.Secondary,
        },
        meetingStatsRow: {
            display: 'flex',
            flexWrap: 'wrap',
            gap: 24,
            borderTop: '1px solid #E2E7EE',
            marginTop: 14,
            paddingTop: 14,
        },
        meetingStat: {
            display: 'flex',
            flexDirection: 'column',
            gap: 4,
            minWidth: 100,
        },
        meetingStatLabel: {
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            color: mainTheme.TextColor.Secondary,
        },
        meetingStatValue: {
            fontSize: 14,
            fontWeight: 600,
            color: mainTheme.TextColor.Primary,
            display: 'flex',
            alignItems: 'center',
            gap: 6,
        },
        meetingStatValuePositive: {
            color: 'var(--rv-accent-green)',
        },
        meetingStatDot: {
            width: 7,
            height: 7,
            borderRadius: '50%',
            flexShrink: 0,
            backgroundColor: '#B8C0CC',
        },
        meetingStatDotPositive: {
            backgroundColor: 'var(--rv-accent-green)',
        },
        meetingNotesBlock: {
            marginTop: 14,
        },
        meetingNotesText: {
            fontSize: 14,
            color: mainTheme.TextColor.Primary,
            marginTop: 4,
            lineHeight: 1.55,
        },
        assignButton: {
            display: 'block',
            width: 'fit-content',
            marginLeft: 'auto',
            marginTop: 14,
            textTransform: 'none'
        },
        headerEditButton: {
            minWidth: 0,
            padding: '0 8px',
            lineHeight: '22px',
            textTransform: 'none',
        }
    })
);

// Mirrors convertMentionInCommentToHtml's own @[Display](id) parsing (below, inside
// HistoryActivity itself), but to plain text instead of an HTML <span> - a mention copies as
// just the person/team's name, matching what's actually shown on screen.
const stripMentionsToPlainText = (comment: string, label?: string): string => {
    const withoutMentions = comment.replace(/@\[(.+?)\]\(.+?\)/g, (_match, display: string) => display.split('|')[0]);
    return label ? `${label}: ${withoutMentions}` : withoutMentions;
};

// Explicit bug report: a long follow-up/attendance note (sometimes a full page of text) made
// the whole activity timeline unusably tall, forcing a scroll past it just to see anything
// after it. Collapses to a fixed preview height with a "Read more" toggle; expanding grows to a
// larger (but still capped) height with its own internal scroll, rather than fully unrolling to
// however long the note happens to be. A copy button pulls the plain text out without having to
// fight manual text selection across all that scrolling. A plain, standalone component (not
// nested inside HistoryActivity itself) so its own isExpanded state isn't reset by every
// re-render of that much larger parent - the CSS below still applies via the plain "description"
// class name regardless, since those rules are scoped by actual DOM ancestry (.detailsContainer
// .description), not by which component renders the element.
const NOTE_COLLAPSED_HEIGHT = 110;
// Explicit request: once a note is long enough to need Read More anyway, there's no reason for
// the collapsed preview to stay full-size - shrinking it to 60% gives a shorter preview without
// changing anything about notes that already fit within NOTE_COLLAPSED_HEIGHT on their own (those
// never show Read More, so this value never applies to them - see the inline style override
// below, only applied once isOverflowing is confirmed true).
const NOTE_COLLAPSED_HEIGHT_OVERFLOWING = Math.round(NOTE_COLLAPSED_HEIGHT * 0.6);
const NOTE_EXPANDED_HEIGHT = 320;

const useExpandableNoteStyles = makeStyles((theme: Theme) =>
    createStyles({
        // Explicit bug report: the actions row (Show less + copy) rendered as a tall box beside
        // the text instead of a thin strip below it, and the expanded text showed the browser's
        // own chunky default scrollbar instead of the app's thin one (App.css's own global
        // *::-webkit-scrollbar rule). Both read as this component inheriting some ambient flex/
        // scrollbar context from wherever it happens to be dropped in, rather than being fully
        // self-contained - root forces column stacking explicitly regardless of any such
        // context, and contentExpanded repeats the thin-scrollbar rule locally rather than
        // assuming the global one reaches an element rendered via dangerouslySetInnerHTML.
        root: {
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
        },
        contentBox: {
            position: 'relative',
            width: '100%',
        },
        contentCollapsed: {
            maxHeight: NOTE_COLLAPSED_HEIGHT,
            overflow: 'hidden',
        },
        contentExpanded: {
            maxHeight: NOTE_EXPANDED_HEIGHT,
            overflowY: 'auto',
            scrollbarWidth: 'thin',
            '&::-webkit-scrollbar': {
                width: 4,
            },
            '&::-webkit-scrollbar-track': {
                background: 'transparent',
            },
            '&::-webkit-scrollbar-thumb': {
                background: '#a8afb9',
                borderRadius: 2,
            },
        },
        // Explicit request (reference: InternalNotes.png) - a softer, taller fade than before:
        // the old 28px/2-stop gradient read as an abrupt white band over the last line; this
        // starts fading a line earlier and ramps gradually, closer to the reference's look
        // where the whole last line reads as "faded" rather than clipped under a white edge.
        fadeOverlay: {
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: 0,
            height: 38,
            background: 'linear-gradient(to bottom, rgba(255,255,255,0) 0%, rgba(255,255,255,0.55) 45%, #fff 85%)',
            pointerEvents: 'none',
        },
        actions: {
            display: 'flex',
            alignItems: 'center',
            marginTop: 2,
            width: '100%',
            flexShrink: 0,
        },
        readMoreBtn: {
            fontSize: 12,
            padding: '2px 6px',
            minWidth: 'auto',
            textTransform: 'none',
            color: mainTheme.BrandColors.TemplatePrimary,
        },
    }),
);

interface ExpandableNoteProps {
    html: string;
    plainText: string;
    style?: React.CSSProperties;
}

const ExpandableNote: React.FC<ExpandableNoteProps> = ({ html, plainText, style }) => {
    const classes = useExpandableNoteStyles();
    const [isExpanded, setIsExpanded] = useState(false);
    const [isOverflowing, setIsOverflowing] = useState(false);
    const contentRef = useRef<HTMLParagraphElement>(null);

    React.useEffect(() => {
        const node = contentRef.current;
        setIsOverflowing(!!node && node.scrollHeight > NOTE_COLLAPSED_HEIGHT);
    }, [html]);

    return (
        <div className={classes.root}>
            <div className={classes.contentBox}>
                <p
                    ref={contentRef}
                    className={`description ${isExpanded ? classes.contentExpanded : classes.contentCollapsed}`}
                    // Explicit bug report: the plain ".description" class this shares with every
                    // other short card snippet elsewhere in this file also carries its own
                    // max-width: 340px, sized for those - not for this wider, multi-paragraph
                    // note card, where it squeezed the text into an artificially narrow column
                    // (exaggerating the scrollbar's apparent width well beyond its actual 4px).
                    // An inline style always wins over a class-based rule regardless of CSS
                    // source order, unlike a second class name would (the same risk already
                    // fixed once this session via JSS's own $ruleName combinator elsewhere).
                    style={{
                        maxWidth: 'none',
                        ...(!isExpanded && isOverflowing ? { maxHeight: NOTE_COLLAPSED_HEIGHT_OVERFLOWING } : {}),
                        ...style,
                    }}
                    dangerouslySetInnerHTML={{ __html: html }}
                />
                {!isExpanded && isOverflowing && <div className={classes.fadeOverlay} />}
            </div>
            {/* Explicit request: no copy affordance inside the body any more - the card
                header's own copy button (TimelineRow) covers it now. */}
            {isOverflowing && (
                <div className={classes.actions}>
                    <Button
                        size="small"
                        className={classes.readMoreBtn}
                        endIcon={isExpanded ? <ExpandLessIcon style={{ fontSize: 15 }} /> : <ExpandMoreIcon style={{ fontSize: 15 }} />}
                        onClick={() => setIsExpanded(current => !current)}
                    >
                        {isExpanded ? 'Show less' : 'Read more'}
                    </Button>
                </div>
            )}
        </div>
    );
};

// Explicit bug report: Meeting Notes used ExpandableNote's fixed-pixel collapsed height, which
// doesn't land on a line boundary - the collapsed preview showed a half-cut last line under a
// fade that washed out readable text. line-clamp (unlike a pixel maxHeight) always stops at a
// whole line, so this is a dedicated clamp-based treatment instead, scoped to Meeting Notes only
// - ExpandableNote itself is left untouched since 6+ other call sites in this file (followUpNotes,
// natureOfEnquiry) rely on its current pixel-height behaviour and weren't part of this report.
const MEETING_NOTES_CLAMP_LINES = 3;

const useClampedMeetingNotesStyles = makeStyles(() =>
    createStyles({
        wrapper: {
            position: 'relative',
        },
        text: {
            margin: 0,
            marginTop: 8,
            fontSize: 14,
            color: mainTheme.TextColor.Primary,
            lineHeight: 1.55,
            whiteSpace: 'pre-line',
        },
        textClamped: {
            display: '-webkit-box',
            WebkitLineClamp: MEETING_NOTES_CLAMP_LINES,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
        },
        // Explicit bug report lesson (see CLAUDE.md #LESSONS): a line-clamped box must never be
        // measured in its own clamped state, since clientHeight then already equals the clamped
        // height and scrollHeight===clientHeight would wrongly read as "not overflowing" even for
        // a much longer note. This mirror copy is never clamped, so its scrollHeight always
        // reflects the note's true full-text height to compare against the real element's
        // clamped clientHeight - absolutely positioned and hidden so it never affects layout.
        measure: {
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            visibility: 'hidden',
            pointerEvents: 'none',
            zIndex: -1,
        },
        actionsRow: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginTop: 4,
        },
        toggleBtn: {
            fontSize: 12,
            padding: '2px 6px',
            minWidth: 'auto',
            textTransform: 'none',
            color: mainTheme.BrandColors.TemplatePrimary,
        },
    }),
);

interface ClampedMeetingNotesProps {
    text: string;
}

const ClampedMeetingNotes: React.FC<ClampedMeetingNotesProps> = ({ text }) => {
    const classes = useClampedMeetingNotesStyles();
    const [isExpanded, setIsExpanded] = useState(false);
    const [isOverflowing, setIsOverflowing] = useState(false);
    const measureRef = useRef<HTMLParagraphElement>(null);

    const measureOverflow = () => {
        const node = measureRef.current;
        if (node) {
            setIsOverflowing(node.scrollHeight > node.clientHeight + 1);
        }
    };

    React.useLayoutEffect(() => {
        measureOverflow();
        // tslint:disable-next-line: align
    }, [text]);

    React.useEffect(() => {
        window.addEventListener('resize', measureOverflow);
        return () => window.removeEventListener('resize', measureOverflow);
        // tslint:disable-next-line: align
    }, [text]);

    React.useEffect(() => {
        // tslint:disable-next-line: no-any
        const fontSet = (document as any).fonts;
        if (fontSet && typeof fontSet.ready?.then === 'function') {
            fontSet.ready.then(() => measureOverflow());
        }
        // tslint:disable-next-line: align
    }, []);

    return (
        <div className={classes.wrapper}>
            <p ref={measureRef} className={clsx(classes.text, classes.textClamped, classes.measure)} style={{ marginTop: 0 }} aria-hidden="true">
                {text}
            </p>
            <p className={clsx(classes.text, !isExpanded && classes.textClamped)}>
                {text}
            </p>
            {isOverflowing && (
                <div className={classes.actionsRow}>
                    <Button
                        size="small"
                        className={classes.toggleBtn}
                        endIcon={isExpanded ? <ExpandLessIcon style={{ fontSize: 15 }} /> : <ExpandMoreIcon style={{ fontSize: 15 }} />}
                        onClick={() => setIsExpanded(current => !current)}
                    >
                        {isExpanded ? 'Show less' : 'Show more'}
                    </Button>
                </div>
            )}
        </div>
    );
};

// Explicit request: "Previous Notes: <bold text> (date)" ran straight into the card's own body
// text, and the bold made a long pasted paragraph unreadable. Now a quiet, separate block - a small
// label with the date, then the text in normal weight/secondary colour, clamped to
// MEETING_NOTES_CLAMP_LINES with Show more/less (reuses ClampedMeetingNotes). Same text and date as
// before, shown at all 5 places the old line was.
const usePreviousNotesStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            marginTop: 12,
            padding: theme.spacing(1, 1.5),
            borderRadius: 8,
            backgroundColor: '#f7f8fc',
            textAlign: 'start',
            // Explicit bug report: it sat flush against the "Auto Follow Up" notice row below it -
            // space only when something follows, so it adds nothing at the bottom of a card.
            '&:not(:last-child)': {
                marginBottom: 8,
            },
            // Beats ClampedMeetingNotes's own 14px / primary-colour text rule (one class deeper) so
            // this reads as secondary to the note above it.
            '& p': {
                marginTop: 2,
                fontSize: 13,
                fontWeight: 400,
                color: theme.palette.text.secondary,
            },
        },
        heading: {
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            fontSize: 11,
            fontWeight: 800,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            color: theme.palette.text.secondary,
            '& svg': {
                fontSize: 14,
            },
        },
        headingDate: {
            fontWeight: 600,
            letterSpacing: 0,
            textTransform: 'none',
            color: mainTheme.TextColor.Tertiary,
        },
        // Explicit request: Reason / Referred to are one line - the label and its value side by side - instead of a label over a value.
        singleLine: {
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing(1.25),
            marginTop: 0,
            '& p': {
                flex: 1,
                minWidth: 0,
                margin: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
            },
            '& $heading': {
                flexShrink: 0,
            },
        },
    }),
);

interface PreviousNotesProps {
    notes: string;
    createdDate?: Date | string;
}

const PreviousNotes: React.FC<PreviousNotesProps> = ({ notes, createdDate }) => {
    const classes = usePreviousNotesStyles();
    return (
        <div className={classes.root}>
            <div className={classes.heading}>
                <HistoryOutlinedIcon />
                Previous notes
                {createdDate && <span className={classes.headingDate}>· {moment(createdDate).format('DD MMM YYYY')}</span>}
            </div>
            <ClampedMeetingNotes text={notes} />
        </div>
    );
};

// Explicit request: the Not Proceeding card's Reason and Referred to use the same quiet block as Previous notes - a small icon and label, then the
// value - so the card keeps one look for the extra details under its note.
const LabelledBlock: React.FC<{ icon: React.ReactNode; heading: string }> = ({ icon, heading, children }) => {
    const classes = usePreviousNotesStyles();
    return (
        <div className={clsx(classes.root, classes.singleLine)}>
            <div className={classes.heading}>
                {icon}
                {heading}
            </div>
            <p>{children}</p>
        </div>
    );
};

// Hoisted to module scope (was a local const in the main component) so TimelineRow below can
// use the same styled Tooltip for its own hover date/time - it has no dependency on the main
// component's props/state, just the theme withStyles injects, so hoisting changes nothing about
// its own behaviour for the main component's existing usages (filter chip tooltips).
const LightTooltip = withStyles((theme: Theme) => ({
    arrow: {
        color: theme.palette.common.white,
        '&::before': {
            boxShadow: theme.shadows[1],
        }
    },
    tooltip: {
      backgroundColor: theme.palette.common.white,
      color: 'rgba(0, 0, 0, 0.87)',
      boxShadow: theme.shadows[1],
      fontSize: 11,
    },
}))(Tooltip);

interface TimelineRowProps {
    icon: { node: React.ReactNode; fg: string; bg: string };
    typeLabel: string;
    author?: string;
    initials?: string;
    timeDisplay?: string;
    timeTooltip?: string;
    isUpcoming?: boolean;
    // Explicit request: the Create Matter/Client "didn't finish" card - solid red-tinted border instead of the upcoming card's dashed one.
    isFailed?: boolean;
    body?: React.ReactNode;
    quietContent?: React.ReactNode;
    // Explicit request: an action that lives in the card's own header (next to the time), e.g. a Meeting
    // Booked card's Edit button, instead of at the bottom of its body.
    headerAction?: React.ReactNode;
}

// Explicit request (redesign): the shared row shell every timeline entry renders through - rail
// node + a card (type label/author/time header, body section only when there's real content).
// Explicit correction: only assignment (quietContent set, see getAssignEnquiryQuietContent)
// renders as a single quiet line instead - every other entry is always a card, even with an
// empty/no body (follow-up activities should still look like cards, per the task's own
// explanation). hasBody is now a direct, stable read of "was a body passed in" - an earlier
// version measured the rendered body's own DOM height (useLayoutEffect) to tell a visually-empty
// body (e.g. one whose only content was the now-hidden duplicate author name) from a real one,
// but that measurement reran on every parent re-render (getDetailContent returns a fresh body
// element each call, so the effect's own [body] dependency always "changed"), and could settle
// on a stale/wrong reading when an unrelated re-render (e.g. a Follow Up Action chip click,
// which simply toggles isSubmitting) raced it - explicit bug report: cards intermittently lost
// their body content after any Follow Up panel interaction. Per the correction above, a visually
// -empty body should still show its (empty-looking) body section rather than be hidden, so the
// measurement added no value worth that fragility - removed. Callers (activityContent/
// enquiryCreated/notProceeding) still derive icon/typeLabel/author/timestamp/body from exactly
// the same fields/conditions as before - this only changes how that same data is laid out.
const TimelineRow: React.FC<TimelineRowProps> = ({ icon, typeLabel, author, initials, timeDisplay, timeTooltip, isUpcoming, isFailed, body, quietContent, headerAction }) => {
    const classes = useStyles();
    const bodyRef = useRef<HTMLDivElement>(null);
    const [isCopied, setIsCopied] = useState(false);

    const hasBody = !!body;
    // Assignment only - and only when it genuinely has no body (no follow-up note attached;
    // see getDetailContent's own assign-enquiry branch, which keeps the card when one exists).
    const isQuietLine = !!quietContent && !body;

    // Explicit request: the header's copy button copies the body only, not the header's own
    // type label/author/time - reuses bodyRef (the body wrapper's own ref). innerText already
    // skips display:none nodes (the hidden '.author'/'.authorNameTitle' duplicates), so this
    // needs no per-branch text-extraction logic of its own.
    const onCopyBody = () => {
        if (!bodyRef.current) {
            return;
        }
        navigator.clipboard.writeText(bodyRef.current.innerText)
            .then(() => {
                setIsCopied(true);
                setTimeout(() => setIsCopied(false), 1500);
            })
            // tslint:disable-next-line: no-empty
            .catch(() => {});
    };

    return (
        <div className={clsx(classes.historyContainer, isQuietLine && classes.historyContainerCentered)}>
            {IS_RENDER_COUNT_ENABLED && <RenderCountBadge label={`TimelineRow · ${typeLabel}`} />}
            <div className="connector" />
            <div className={classes.historyIcon}>
                {icon.node}
            </div>
            {!isQuietLine ? (
                <div className={clsx(classes.activityCard, isUpcoming && classes.activityCardUpcoming, isFailed && classes.activityCardFailed)}>
                    <div className={clsx(classes.cardHeaderRow, hasBody && classes.cardHeaderRowWithBody)}>
                        <span className={classes.cardTypeLabel} style={{ color: icon.fg }}>{typeLabel}</span>
                        {author && (
                            <span className={classes.cardAuthor}>
                                <span className={classes.cardAuthorByLabel}>by</span>
                                <span className={classes.cardAuthorAvatar}>{initials}</span>
                                <span className={classes.cardAuthorName}>{author}</span>
                            </span>
                        )}
                        <span className={classes.cardHeaderRight}>
                            {headerAction}
                            {hasBody && (
                                <Tooltip title={isCopied ? 'Copied' : 'Copy'}>
                                    <IconButton
                                        size="small"
                                        className={classes.cardCopyBtn}
                                        onClick={onCopyBody}
                                    >
                                        {isCopied ? <CheckIcon fontSize="small" /> : <FileCopyOutlinedIcon fontSize="small" />}
                                    </IconButton>
                                </Tooltip>
                            )}
                            {timeDisplay && (
                                <LightTooltip arrow={true} title={timeTooltip || ''}>
                                    <span className={isUpcoming ? classes.upcomingPill : classes.cardTime}>{timeDisplay}</span>
                                </LightTooltip>
                            )}
                        </span>
                    </div>
                    {hasBody && (
                        <div className={classes.contentDetails}>
                            <div ref={bodyRef}>
                                {body}
                            </div>
                        </div>
                    )}
                </div>
            ) : (
                <div className={clsx(classes.quietEventText, isQuietLine && classes.quietEventAssigned)}>
                    <span>
                        {quietContent}
                    </span>
                    {timeDisplay && (
                        <LightTooltip arrow={true} title={timeTooltip || ''}>
                            <span className={classes.cardTime}>{timeDisplay}</span>
                        </LightTooltip>
                    )}
                </div>
            )}
        </div>
    );
};

export enum HistoryActivityWidth {
    FULL_WIDTH = 0,
    NORMAL
}
export type ActivityCategory = 'calls' | 'emailsAndSms' | 'meetings' | 'notes' | 'matterAndClient' | 'followUpsDue';

// Mirrors getHistoryAvatar's own branches (same conditions, same order) - grouped into the 6
// categories the activity filter chips below use, rather than one bucket per icon colour.
// Exported (hoisted to module scope - pure function of task fields only, no closure needed) so
// the enquiry header's Journey widget can reuse the same categorisation instead of duplicating
// it, per the explicit request that it use "the same query & data from the activity tab".
export const getActivityCategory = (task: TaskDetails): ActivityCategory => {
    if (task.taskTypeDescription
        && (task.taskTypeDescription.toLowerCase() === 'enquiry follow up' || task.taskTypeDescription.toLowerCase() === 'book a meeting')
        && !task.completedDate && task.dueDate
        && !(task.followUpAction && task.followUpAction.toLowerCase() === 're-open enquiry')) {
        return 'followUpsDue';
    }

    if (task.taskTypeDescription &&
        (task.taskTypeDescription.toLowerCase() === 'create client' || task.taskTypeDescription.toLowerCase() === 'create matter')) {
        return 'matterAndClient';
    } else if (task.taskTypeDescription && task.taskTypeDescription.toLowerCase() === 'assign enquiry') {
        return 'matterAndClient';
    } else if (task.taskTypeInternalDescription && task.taskTypeInternalDescription === 'LinkMatter') {
        return 'matterAndClient';
    } else if (task.taskTypeInternalDescription && task.taskTypeInternalDescription === 'BookMeeting') {
        return 'meetings';
    } else if (task.followUpActionSystemName && task.followUpActionSystemName === 'HadMeeting') {
        return 'meetings';
    } else {
        // Explicit bug report: an action such as "Send SMS Questions" / "Send an Email Questions" matches none of the name checks below and fell
        // into the last-resort Meetings group. Its system action says what it is, whatever it is called (same check getHistoryAvatar uses).
        if (task.systemAction === 'CreateEmail' || task.systemAction === 'CreateSMS') {
            return 'emailsAndSms';
        } else if (task.followUpAction
            && (task.followUpAction.toLowerCase() === 'email' || task.followUpAction.toLowerCase() === 'emailed')) {
            return 'emailsAndSms';
        } else if (task.followUpActionSystemName && task.followUpActionSystemName.toLowerCase() === 'sendemail') {
            return 'emailsAndSms';
        } else if (task.followUpActionSystemName && task.followUpActionSystemName.toLowerCase() === 'sms') {
            return 'emailsAndSms';
        } else if (task.followUpActionSystemName && task.followUpActionSystemName.toLowerCase() === 'senddataform') {
            return 'emailsAndSms';
        } else if (task.followUpAction &&
            (task.followUpAction.toLowerCase() === 'phone call' || task.followUpAction.toLowerCase() === 'phoned')) {
            return 'calls';
        } else if (task.followUpAction && task.followUpAction.toLowerCase() === 'sms') {
            return 'emailsAndSms';
        } else if (task.followUpAction && task.followUpAction.toLowerCase() === 'internal note') {
            return 'notes';
        } else if (task.followUpAction && task.followUpAction.toLowerCase() === 'book a meeting') {
            return 'meetings';
        } else if (task.followUpAction && task.followUpAction.toLowerCase() === 're-open enquiry') {
            return 'matterAndClient';
        } else if (task.followUpAction && task.followUpAction.toLowerCase() === 'unlink matter') {
            return 'matterAndClient';
        } else { // Meeting - same fallback getHistoryAvatar's own final else uses
            return 'meetings';
        }
    }
};

// Explicit request: a tooltip on each filter chip listing which follow-up action values it
// groups together - mirrors getActivityCategory's own branches below (same groupings, same
// order) so this stays in sync with what the filter actually matches.
const ACTIVITY_FILTER_OPTIONS: { value: ActivityCategory; label: string; includes: string[] }[] = [
    { value: 'calls', label: 'Calls', includes: ['Phone Call'] },
    { value: 'emailsAndSms', label: 'Emails & SMS', includes: ['Email', 'SMS', 'Data Form Sent'] },
    { value: 'meetings', label: 'Meetings', includes: ['Book a Meeting', 'Had Meeting'] },
    { value: 'notes', label: 'Notes', includes: ['Internal Note'] },
    {
        value: 'matterAndClient', label: 'Matter & Client',
        includes: ['Create Client', 'Create Matter', 'Assign Enquiry', 'Link Matter', 'Unlink Matter', 'Re-open Enquiry'],
    },
    { value: 'followUpsDue', label: 'Follow-ups Due', includes: ['Any due, not-yet-completed follow-up or meeting'] },
];

interface HistoryActivityProps {
    className?: string;
    guid?: string;
    sourceSystem: string;
    enquiryDetail: EnquiryDetail;
    // sortColumn: string | null;
    sortDirection: string | null;
    // isRefreshData?: boolean;
    width?: HistoryActivityWidth;
    onCreateClient?: (isCreateClient: boolean) => void;
    autoScroll?: boolean;
    currency: string;
    timezone: TimezoneOption[];
    leadDetailsHistory: TaskSummaryList;
    onAssign?: (task: TaskDetails) => void;
    canCreateMatter: boolean;
    onRemoveAutoFollowUp?: (taskGuid: string) => void;
    isSubmitting?: boolean | null;
}

interface HistoryActivityState {
    showEmailContent: boolean;
    showMessageContent: boolean;
    taskGuid?: string | null;
    // isLoading: boolean;
}

// tslint:disable-next-line: no-anyW
// Explicit performance report: this re-rendered on every keystroke anywhere in the enquiry form,
// since its parent (ActivityTabSection in EnquiryFormDrawer.tsx) used to be called as a plain
// closure with no component boundary - memo here is extra, belt-and-braces protection for any
// other call site, now that that parent boundary is fixed.
export const HistoryActivity: React.FC<HistoryActivityProps> = React.memo(props => {
    
    const { t } = useTranslation() as {
        t: (key: keyof TranslationTextList) => string;
    };
    
    const classes = useStyles();

    // const [leadDetailsHistory, setLeadDetailsHistory] = useState<TaskSummaryList>({
    //     tasks: [],
    //     actualRowCountForSearch: 0,
    //     recordCount: 0
    // });

    const [state, setState] = useState<HistoryActivityState>({
        showEmailContent: false,
        showMessageContent: false
        // isLoading: true
    });

    const [activeCategory, setActiveCategory] = useState<ActivityCategory | 'all'>('all');

    // const [currency, setCurrency] = useState<string>('');
    // const [timezone, setTimezone] = useState<TimezoneOption[]>();

    // tslint:disable-next-line: no-any
    const messagesEndRef = useRef<any>(null);

    const scrollToBottom = () => {
        if (messagesEndRef && messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({
                behavior: 'smooth',
            });
        }
    };

    // Explicit bug report: this used to run unconditionally in the render body below (see its
    // own removed call near the component's return) - scrollIntoView finds the nearest
    // scrollable ancestor, which per EnquiryFormDrawer.tsx's own comments is the whole Drawer
    // Paper, not just this list's own box. Since that render-body call had no gating at all, ANY
    // re-render of this component (which happens on practically any state change in the parent
    // drawer, not just new activity arriving) scheduled another scroll-to-bottom of the entire
    // drawer a second later - e.g. clicking the unrelated Expand All button, or Further Details
    // panels populating after picking an Area of Law. Gating on the task count instead only
    // scrolls when activity genuinely changed (new item added, or the first load).
    useEffect(() => {
        if (props.autoScroll) {
            const timeoutId = setTimeout(scrollToBottom, 1000);
            return () => clearTimeout(timeoutId);
        }
        return undefined;
    // tslint:disable-next-line: align
    }, [props.leadDetailsHistory.tasks.length]);

    // useEffect(() => {
        // if (props.guid) {
            // fetchCurrency();
            // fetchLeadDetailsHistory(props.guid);
            // fetchTimezone();
        // }
    // tslint:disable-next-line: align
    // }, [props.isRefreshData, props.guid]);

    // const fetchCurrency = () => {
    //     retrieveCurrencySymbolData(
    //         false,
    //         // tslint:disable-next-line: no-any
    //         (data: any) => onDataCurrency(data),
    //         // tslint:disable-next-line:no-any
    //         function (reason: any): void {
    //           showNotification(null, reason, 'error');
    //     });
    // };

    // const onDataCurrency = (data: CurrencySymbolData) => {
    //     setCurrency(data.options.currencySymbol.symbol);
    // };

    // const fetchTimezone = () => {
    //     retrieveTimezoneOptionData(
    //         false,
    //         // tslint:disable-next-line: no-console
    //         (data) => onRetrieveTimezoneData(data),
    //         // tslint:disable-next-line:no-any
    //         function (reason: any): void {
    //             showNotification(null, reason, 'error');
    //         }
    //     );
    // };

    // const onRetrieveTimezoneData = (data: TimezoneOptionData) => {
    //     setTimezone(data.options.timezoneOption);
    // };
    
    // const fetchLeadDetailsHistory = (guid: string) => {
    //     var leadQueryParams: LeadDetailsHistoryQueryParams = {
    //         offset: null,
    //         first: null,
    //         filter: null,
    //         sortColumn: props.sortColumn,
    //         sortDirection: props.sortDirection,
    //         entityGuidID: guid
    //     };

    //     retrieveLeadDetailsHistoryData(
    //         leadQueryParams,
    //         true,
    //         // tslint:disable-next-line: no-any
    //         (data: any) => onDataRetrieved(data),
    //         // tslint:disable-next-line
    //         function (reason: any): void {
    //             showNotification(null, reason, 'error');
    //         }
    //     );
    // };

    // const onDataRetrieved = (data: LeadDetailsHistoryData) => {
        
    //     setLeadDetailsHistory(data.task.summaryList);
        
    //     const summaryList = data.task.summaryList;

    //     // const task = summaryList.tasks[summaryList.tasks.length - 1];
    //     const createClientTask = summaryList.tasks.find((task: TaskDetails) => {
    //         return (task.taskTypeDescription.toLowerCase() === 'create client' || task.taskTypeDescription.toLowerCase() === 'create matter'  ) 
    //                 && task.taskStatusDescription.toLowerCase() === 'not started';
    //     });

    //     if (createClientTask) {
    //         if (props.onCreateClient) {
    //             props.onCreateClient(true);
    //         }
    //     } else {
    //         if (props.onCreateClient) {
    //             props.onCreateClient(false);
    //         }
    //     }

    //     // const createContactTask = summaryList.tasks.find((task: TaskDetails) => {
    //     //     return task && task.outcome && task.outcome.toLowerCase() === 'not proceeding';
    //     // });

    //     // if (props.onCreateContact) {
    //     //     props.onCreateContact(createContactTask ? true : false);
    //     // }

    //     setState((prevState) => {
    //         return {
    //             ...prevState,
    //             isLoading: false
    //         };
    //     });

    //     if (props.autoScroll !== undefined && props.autoScroll === true) {
    //         setTimeout(scrollToBottom, 1000);
    //     }        
    // };

    const convertMentionInCommentToHtml = (comment: string, label?: string) => {
        let regex = /@\[.+?\]\(.+?\)/gm;
        let displayRegex = /@\[.+?\]/g;
        let idRegex = /\(.+?\)/g;
        let matches = comment.match(regex);
        // tslint:disable-next-line: no-any
        let arr: any = [];
        let output = label ? label + ': ' : '';
        
        // tslint:disable-next-line: no-unused-expression
        if (matches) {
            // tslint:disable-next-line: no-any
            matches.forEach((m: any) => {
                let id = m.match(idRegex)[0].replace('(', '').replace(')', '');
                let display = m.match(displayRegex)[0].replace('@[', '').replace(']', '');

                arr.push({ id: id, display: display });
            });

            let newComment = comment.split(regex);
            
            for (let i = 0; i < newComment.length; i++) {
                const c = newComment[i];
                if (i === newComment.length - 1) {
                    output += c;
                } else {
                    output += c + `<span class="mentioned" mentioned_id="${arr[i].id}">${arr[i].display.split('|')[0]}</span>`;
                }
            }
        } else {
            output = comment.replace(/\n/g, '<br>');
        }
        return output;
    }; 

    const showDateEndOfFromNow = (value: Date | string) => {
        // const utc = moment.utc(value).format();
        // return moment.utc(value).endOf('day').fromNow();
        return moment(value).fromNow();

    };

    // const isDateDue = (value: Date) => {
    //     const currentDate = moment();
    //     const diff = moment(value).diff(currentDate, 'days');

    //     return diff < 0;
    // };

    // Explicit note (redesign): props.width/HistoryActivityWidth is kept as a public prop
    // (EnquiryFormDrawer.tsx/LeadsDetailPage.tsx still pass it) even though nothing in this
    // file reads it any more - the old narrow/normal distinction (getWidth(), removed) doesn't
    // apply to the new layout, where every card is the same full width regardless.

    // Rounded-square icon chip keyed to the activity's own type/channel, replacing the old
    // per-person hashed Avatar colour (stringToColour(task.assignedByName) - decorative, told
    // you nothing about what happened, only who). Same --rv-accent-* tokens (App.css) and
    // 28/6 sizing as EnquiryFormDrawer.tsx's own sectionIconChip (the Details tab's panel
    // header icons) - explicit request to match that size and style here too.
    // Explicit request: the redesigned card header shows a type label in the same colour as
    // this node's own icon (brief: "icon uses the type's foreground colour... colour lives in
    // the node and the type label only"). Returning {node, fg, bg} instead of just the JSX (one
    // mechanical change here, no change to any of getHistoryAvatar's own per-branch icon/colour
    // choices below) lets the header read the same tone every branch already picks, rather than
    // duplicating this whole if/else chain a second time just to re-derive a colour.
    const renderHistoryIcon = (icon: React.ReactNode, bgColor: string, iconColor: string) => ({
        fg: iconColor,
        bg: bgColor,
        node: (
            // Explicit bug report/screenshot: some of the --rv-accent-*-bg tokens (blue/orange/
            // green/grey) are semi-transparent rgba() in App.css, while others (purple/teal/red/
            // indigo) are solid hex - so the connector line behind this chip only bled through
            // for the translucent ones (Created's green, Follow Up Due's orange), never the
            // opaque ones (Assigned's indigo, SMS's purple). z-index stacking was already
            // correct; the chip just wasn't fully opaque. backgroundColor gives it a solid white
            // backdrop, with the token's own colour layered on top via backgroundImage - opaque
            // either way, regardless of which token's own alpha channel is used.
            <div
                className={classes.historyIconChip}
                style={{ backgroundColor: '#fff', backgroundImage: `linear-gradient(${bgColor}, ${bgColor})`, color: iconColor }}
            >
                {icon}
            </div>
        ),
    });

    // A "Book a Meeting" entry is done (no orange colour, no upcoming card) once anything later in the activity
    // shows the meeting was dealt with - the rules live in historyActivityRules.ts.
    const isBookMeetingDone = (task: TaskDetails): boolean => isBookMeetingDoneIn(task, props.leadDetailsHistory.tasks);

    // Explicit request: Assign Enquiry, Create Client and Create Matter that are still to be done are "due", like a Follow Up Due. An assignment is
    // pending while nobody is assigned; a create task only while it has not started or failed (those two have their own cards, see
    // isFailedIntegrationTask and the in-progress wording).
    const isPendingSystemTask = (task: TaskDetails): boolean => {
        if (!task.taskTypeDescription || task.completedDate) {
            return false;
        }
        const taskType = task.taskTypeDescription.toLowerCase();
        if (taskType === 'assign enquiry') {
            return !task.assignedToGuidID;
        }
        if (taskType === 'create client' || taskType === 'create matter') {
            return !task.isIntegrationFailed && !task.isExternalIntegrationInProgress;
        }
        return false;
    };

    // Explicit request: marking an enquiry Proceeding leaves a pending Create Client / Create Matter task, and re-opening the enquiry before that is
    // done makes the backend close it by setting its completed date (nothing is created). Left alone it showed as a finished "Create Matter by
    // <user>". There is no field saying it was closed that way, so it is recognised by what happened with it: it is completed, and a Re-Open
    // finished within a few seconds of it (the same save closes both). A task that was really completed (client or matter created) is not
    // finished at the moment of a re-open, so it keeps its normal card.
    const findReOpenThatCancelledCreateTask = (task: TaskDetails): TaskDetails | undefined => {
        if (!task.completedDate || !task.taskTypeDescription) {
            return undefined;
        }
        const taskType = task.taskTypeDescription.toLowerCase();
        if (taskType !== 'create client' && taskType !== 'create matter' && task.taskTypeInternalDescription !== 'CreateClientFromEnquiry') {
            return undefined;
        }
        const completedAt = new Date(task.completedDate).getTime();
        return props.leadDetailsHistory.tasks.find((otherTask: TaskDetails) => {
            if (otherTask === task || !otherTask.followUpAction || otherTask.followUpAction.toLowerCase() !== 're-open enquiry') {
                return false;
            }
            const reOpenedAt = otherTask.completedDate ?? otherTask.createdDate;
            return !!reOpenedAt && Math.abs(new Date(reOpenedAt).getTime() - completedAt) <= 5000;
        });
    };
    const isCreateTaskCancelledByReOpen = (task: TaskDetails): boolean => !!findReOpenThatCancelledCreateTask(task);

    // Explicit request: a Create Matter / Client / Contact task that is done names the result - "Matter Created" - not the task. Still-to-do ones
    // read "... Due" and ones a re-open closed read "... cancelled" (see isPendingSystemTask, findReOpenThatCancelledCreateTask).
    const getFinishedCreateLabel = (typeLabel: string): string => {
        switch (typeLabel) {
            case 'Create Matter': return 'Matter Created';
            case 'Create Client': return 'Client Created';
            case 'Create Contact': return 'Contact Created';
            default: return typeLabel;
        }
    };

    const getHistoryAvatar = (task: TaskDetails) => {

        // Explicit request: a pending assignment sits on the same dashed orange card as a Follow Up Due, so it gets the same orange clock - its own
        // indigo icon and label looked out of place on that card. Create Client / Create Matter keep their own teal.
        if (isPendingSystemTask(task) && task.taskTypeDescription.toLowerCase() === 'assign enquiry') {
            return renderHistoryIcon(<AccessTimeIcon fontSize="small" />, 'var(--rv-accent-orange-bg)', 'var(--rv-accent-orange)');
        }
        if (isCreateTaskCancelledByReOpen(task)) {
            return renderHistoryIcon(<PermContactCalendarOutlinedIcon fontSize="small" />, 'var(--rv-accent-grey-bg)', 'var(--rv-accent-grey)');
        }

        // Pending / not-yet-actioned follow up - matches getDescription's own "Follow Up Due"
        // condition (below in this file). Explicit bug report/screenshot: without this check,
        // a still-pending reminder fell through every followUpAction branch below (those
        // describe what WAS done, not what's still due) into the generic fallback icon.
        if (task.taskTypeDescription
            && (task.taskTypeDescription.toLowerCase() === 'enquiry follow up' || task.taskTypeDescription.toLowerCase() === 'book a meeting')
            && !task.completedDate && (task.dueDate || !task.followUpAction)
            && !isBookMeetingDone(task)
            && !(task.followUpAction && task.followUpAction.toLowerCase() === 're-open enquiry')) {
            return renderHistoryIcon(<AccessTimeIcon fontSize="small" />, 'var(--rv-accent-orange-bg)', 'var(--rv-accent-orange)');
        }

        if (task.taskTypeDescription &&
            (task.taskTypeDescription.toLowerCase() === 'create client' || task.taskTypeDescription.toLowerCase() === 'create matter')) {
            return renderHistoryIcon(<PermContactCalendarOutlinedIcon fontSize="small" />, 'var(--rv-accent-teal-bg)', 'var(--rv-accent-teal)');
        } else if (task.taskTypeDescription && task.taskTypeDescription.toLowerCase() === 'assign enquiry') {
            return renderHistoryIcon(<AssignmentIndOutlinedIcon fontSize="small" />, 'var(--rv-accent-indigo-bg)', 'var(--rv-accent-indigo)');
        } else if (task.taskTypeInternalDescription && task.taskTypeInternalDescription === 'LinkMatter') {
            // Same teal family as "create client"/"create matter" above - this is also a
            // matter-related connection, and the positive counterpart to Unlink Matter's red
            // below (LinkOutlinedIcon vs LinkOffOutlinedIcon).
            return renderHistoryIcon(<LinkOutlinedIcon fontSize="small" />, 'var(--rv-accent-teal-bg)', 'var(--rv-accent-teal)');
        } else if (task.taskTypeInternalDescription && task.taskTypeInternalDescription === 'BookMeeting') {
            if (isBookMeetingDone(task)) {
                return renderHistoryIcon(<EventOutlinedIcon fontSize="small" />, 'var(--rv-accent-green-bg)', 'var(--rv-accent-green)');
            }
            return renderHistoryIcon(<EventOutlinedIcon fontSize="small" />, 'var(--rv-accent-orange-bg)', 'var(--rv-accent-orange)');
        } else if (task.followUpActionSystemName && task.followUpActionSystemName === 'HadMeeting') {
            return renderHistoryIcon(<SupervisorAccountOutlinedIcon fontSize="small" />, 'var(--rv-accent-orange-bg)', 'var(--rv-accent-orange)');
        } else { // enquiry follow up
            // Explicit bug report: actions like "Send SMS Questions" / "Send an Email Questions" matched none of the name checks below and
            // showed the last-resort icon. What the entry does (its system action) is what decides, so every Create Email / Create SMS action
            // gets the icon of its kind whatever it is called.
            if (task.systemAction === 'CreateSMS') {
                return renderHistoryIcon(<SmsOutlinedIcon fontSize="small" />, 'var(--rv-accent-purple-bg)', 'var(--rv-accent-purple)');
            } else if (task.systemAction === 'CreateEmail') {
                return renderHistoryIcon(<DraftsOutlinedIcon fontSize="small" />, 'var(--rv-accent-blue-bg)', 'var(--rv-accent-blue)');
            } else if (task.followUpAction
                && (task.followUpAction.toLowerCase() === 'email' || task.followUpAction.toLowerCase() === 'emailed')) {
                return renderHistoryIcon(<MailOutlinedIcon fontSize="small" />, 'var(--rv-accent-blue-bg)', 'var(--rv-accent-blue)');
            } else if (task.followUpActionSystemName && task.followUpActionSystemName.toLowerCase() === 'sendemail') {
                return renderHistoryIcon(<DraftsOutlinedIcon fontSize="small" />, 'var(--rv-accent-blue-bg)', 'var(--rv-accent-blue)');
            } else if (task.followUpActionSystemName && task.followUpActionSystemName.toLowerCase() === 'sms') {
                // Was DraftsOutlinedIcon (same as SendEmail above) - explicit bug report: an
                // SMS entry read as another email in the timeline. Also was checking for
                // 'sendsms', which doesn't match the real value - confirmed as exactly "SMS".
                return renderHistoryIcon(<SmsOutlinedIcon fontSize="small" />, 'var(--rv-accent-purple-bg)', 'var(--rv-accent-purple)');
            } else if (task.followUpActionSystemName && task.followUpActionSystemName.toLowerCase() === 'senddataform') {
                return renderHistoryIcon(<AssignmentOutlinedIcon fontSize="small" />, 'var(--rv-accent-teal-bg)', 'var(--rv-accent-teal)');
            } else if (task.followUpAction &&
                (task.followUpAction.toLowerCase() === 'phone call' || task.followUpAction.toLowerCase() === 'phoned')) {
                return renderHistoryIcon(<CallOutlinedIcon fontSize="small" />, 'var(--rv-accent-green-bg)', 'var(--rv-accent-green)');
            } else if (task.followUpAction && task.followUpAction.toLowerCase() === 'sms') {
                return renderHistoryIcon(<SmsOutlinedIcon fontSize="small" />, 'var(--rv-accent-purple-bg)', 'var(--rv-accent-purple)');
            } else if (task.followUpAction && task.followUpAction.toLowerCase() === 'internal note') {
                return renderHistoryIcon(<NotesOutlinedIcon fontSize="small" />, 'var(--rv-accent-grey-bg)', 'var(--rv-accent-grey)');
            } else if (task.followUpAction && task.followUpAction.toLowerCase() === 'book a meeting') { // meeting task
                return renderHistoryIcon(<PeopleOutlineOutlinedIcon fontSize="small" />, 'var(--rv-accent-orange-bg)', 'var(--rv-accent-orange)');
            } else if (task.followUpAction && task.followUpAction.toLowerCase() === 're-open enquiry') {
                // Was CheckOutlinedIcon - a plain checkmark usually reads as "done/completed",
                // arguably the opposite of reopening a closed enquiry. Same icon as the Re-Open button
                // (ActivityTabSection.tsx), so the action and its entry in the timeline match.
                return renderHistoryIcon(<RestoreIcon fontSize="small" />, 'var(--rv-accent-green-bg)', 'var(--rv-accent-green)');
            } else if (task.followUpAction && task.followUpAction.toLowerCase() === 'unlink matter') {
                return renderHistoryIcon(<LinkOffOutlinedIcon fontSize="small" />, 'var(--rv-accent-red-bg)', 'var(--rv-accent-red)');
            } else { // Meeting
                return renderHistoryIcon(<NotListedLocationOutlinedIcon fontSize="small" />, 'var(--rv-accent-grey-bg)', 'var(--rv-accent-grey)');
            }
        }
    };

    // Explicit request (redesign): replaces the old getHistoryDate (same priority order, same
    // fields: BookMeeting's createdDate, else completedDate, else dueDateUTC - no new rule),
    // now exposed as a plain Date instead of pre-rendered relative-time JSX, since the new
    // header shows an absolute HH:mm clock time and this same value also drives day grouping.
    const getTaskTimestamp = (task: TaskDetails): Date | null => {
        if (task.taskTypeInternalDescription === 'BookMeeting' && task.createdDate) {
            return task.createdDate;
        }
        if (task.completedDate) {
            return task.completedDate;
        }
        if (task.dueDateUTC) {
            return convertToLocalTimeZone(task.dueDateUTC.toString());
        }
        return null;
    };

    // task.assignedByName is the field every getDetailContent branch's own (now-hidden,
    // see contentDetails' ".author" rule) "Created by:"/"Sent by:" line already used;
    // task.ownerName is the equivalent field the handful of integration-status branches use
    // instead (no assignedByName on those). Same two fields, just surfaced for the new header.
    const getTaskAuthorName = (task: TaskDetails): string | undefined => task.assignedByName || task.ownerName || undefined;

    const getInitials = (name?: string): string => {
        if (!name) {
            return '';
        }
        const parts = name.trim().split(/\s+/).filter(Boolean);
        if (parts.length === 0) {
            return '';
        }
        if (parts.length === 1) {
            return parts[0].charAt(0).toUpperCase();
        }
        return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
    };

    // Explicit request: show relative time ("6 minutes ago"/"in 4 days") as the primary display
    // again, like the old implementation's getHistoryDate did, with the full date/time on hover
    // (getTimeTooltip) rather than an always-visible absolute clock time.
    const getTimeDisplay = (date: Date | null): string | undefined => date ? showDateEndOfFromNow(date) : undefined;
    const getTimeTooltip = (date: Date | null): string | undefined => date ? moment(date).format('Do MMM YYYY hh:mm a') : undefined;

    // Explicit request: a day header before each new calendar day - "TODAY · <day> <date>" for
    // today (TODAY in the primary colour), "<DAY> <DATE> · <relative>" otherwise, e.g.
    // "WEDNESDAY 7 OCTOBER · 2 days ago".
    const formatDayHeaderLabel = (date: Date): React.ReactNode => {
        const momentDate = moment(date);
        const dayAndDate = momentDate.format('dddd D MMMM').toUpperCase();
        if (momentDate.isSame(moment(), 'day')) {
            return (
                <>
                    <span className="today">TODAY</span> · {dayAndDate}
                </>
            );
        }
        return `${dayAndDate} · ${momentDate.fromNow()}`;
    };

    const getDescription = (task: TaskDetails) => {
        let actionedDescription = task.actionedDescription && task.actionedDescription.length > 0 
            ? task.actionedDescription 
            : undefined;

        if (task.taskTypeDescription.toLowerCase() === 'enquiry follow up' || task.taskTypeDescription.toLowerCase() === 'book a meeting') {
            if (task.followUpAction && task.followUpAction.toLowerCase() === 're-open enquiry') {
                return actionedDescription ? actionedDescription : 'Re-Opened';
            } else if (task.completedDate) {
                return actionedDescription ? actionedDescription : task.followUpAction;
            } else if (task.dueDate) {
                return actionedDescription ? actionedDescription : 'Follow Up Due';
            } else {
                // Explicit bug report: the pending follow-up Re-Open leaves behind has neither a due date nor an action,
                // so this returned nothing and the card read as just "by Administrator". Still pending -> Follow Up Due.
                return actionedDescription ? actionedDescription : (task.followUpAction || 'Follow Up Due');
            }
        } else {
            const enquiryText = task.isKeyOpportunity ? t('KeyOpportunity') : t('Enquiry');

            if (task.enquiryLinkedMatter) {
                return 'Link Matter';
            }

            let taskTypeDescription = task.taskTypeDescription.replace('Enquiry', enquiryText);

            if (taskTypeDescription === 'Create Matter' && props.enquiryDetail.isMatterCreated === false && props.enquiryDetail.isContactCreated === true && 
                props.canCreateMatter === false) {
                taskTypeDescription = 'Create Contact';
            }

            return taskTypeDescription;
        }
    };    

    const onAssign = (task: TaskDetails) => () => {
        if (props.onAssign) {
            props.onAssign(task);
        }
    };

    // Explicit request (reference: MeetingBooked.png) - replaces the old getAssignedTo's single
    // run-on sentence with the same underlying fields (staff/assignedToName/firmOffice/
    // bookMeetingStartDateUTC/timezone), split into the reference's title/date-time/location
    // layout. Same two gating conditions as before (isAssignedToTeam+assignedToName vs.
    // !isAssignedToTeam+staff - neither match still shows nothing, exactly as before).
    const getMeetingInfo = (task: TaskDetails): { title?: React.ReactNode; dateTimeText?: string; locationText?: string } => {

        let uiUtcOffset = new Date().getTimezoneOffset() * -1;

        let uiTimeZoneDescription = Intl.DateTimeFormat().resolvedOptions().timeZone;

        if (props.timezone) {
            let uiTimeZone = props.timezone.find((item: TimezoneOption) => {
                return item.baseUtcOffset === uiUtcOffset;
            });

            if (uiTimeZone) {
                uiTimeZoneDescription = uiTimeZone.timezoneValue;
            }
        }

        const isTeamMeeting = !!(task.isAssignedToTeam && task.assignedToName);
        const isStaffMeeting = !task.isAssignedToTeam && !!task.staff;

        if (!isTeamMeeting && !isStaffMeeting) {
            return {};
        }

        const dateTimeText = task.bookMeetingStartDateUTC
            ? moment(convertToLocalTimeZone(task.bookMeetingStartDateUTC.toString())).format('dddd D MMMM YYYY · h:mm a')
            : undefined;

        const locationText = (task.firmOffice || task.timezone)
            ? [task.firmOffice, task.timezone ? uiTimeZoneDescription : undefined].filter(Boolean).join(' · ')
            : undefined;

        if (isTeamMeeting) {
            return {
                title: <h5 className={classes.meetingTitle}>Meeting scheduled</h5>,
                dateTimeText,
                locationText,
            };
        }

        return {
            title: (
                <h5 className={classes.meetingTitle}>
                    Meeting with <span className="assigneeChip">{task.staff}</span>
                </h5>
            ),
            dateTimeText,
            locationText,
        };
    };

    // Extracted so getDetailContent's own "would this branch render literally nothing" checks
    // (see the dueDate+completedDate and assignedByName+completedDate branches below) can ask
    // the exact same question getAutoFollowUpRemoved/getAutFollowUp already answer internally,
    // instead of duplicating - and risking drifting from - these conditions a second time.
    const hasAutoFollowUpRemovedContent = (isAutoFollowUpRemoved: boolean | null, isAutoFollowUpSent: boolean | null): boolean =>
        isAutoFollowUpSent == null && !!isAutoFollowUpRemoved; // removed and not sent

    const hasAutoFollowUpContent = (task: TaskDetails): boolean =>
        !!task.isAutoFollowUp && (task.isAutoFollowUpRemoved == null || (task.isAutoFollowUpRemoved === true && task.isAutoFollowUpSent === true));

    const getAutoFollowUpRemoved = (isAutoFollowUpRemoved: boolean | null, isAutoFollowUpSent: boolean | null) => {
        if (hasAutoFollowUpRemovedContent(isAutoFollowUpRemoved, isAutoFollowUpSent)) {
            return (
                <div>
                    <p className={clsx(classes.autoFollowUpNotice, classes.autoFollowUpNoticeRemoved)}>
                        <AutorenewOutlinedIcon className={classes.autoFollowUpNoticeIcon} />
                        <span className={classes.autoFollowUpNoticeText}>Auto Follow Up Removed</span>
                    </p>
                </div>
            );
        }

        return (<></>);
    };

    const getAutFollowUp = (task: TaskDetails) => {
        return (
            hasAutoFollowUpContent(task) && (
                <div>
                    <p className={classes.autoFollowUpNotice}>
                        <AutorenewOutlinedIcon className={classes.autoFollowUpNoticeIcon} />
                        <span className={classes.autoFollowUpNoticeText}>
                            Auto Follow Up: <span className={classes.autoFollowUpTemplateName}>{task.emailTemplateName}</span>
                        </span>
                        {task.isAutoFollowUpSent == null && (<Button
                            color="primary"
                            size="small"
                            className={classes.autoFollowUpRemoveButton}
                            onClick={() => {
                                if (props.onRemoveAutoFollowUp) {
                                    props.onRemoveAutoFollowUp(task.taskGuidID);
                                }
                            }}
                            disabled={props.isSubmitting ?? false}
                        >
                            Remove
                        </Button>
                        )}
                    </p>
                </div>
            )
        );
    };

    // The email / SMS / data form cards list one recipient or several, separated by ';' or ','.
    const splitAddresses = (addresses: string): string[] => addresses.split(/[;,]/).map((address) => address.trim()).filter((address) => !!address);

    // Explicit request (option A): the From / To / Cc / Template panel shared by the Create Email, Create SMS and Send Data Form cards.
    // Returns nothing when none of the fields has a value, so the card does not show an empty panel.
    const renderSentDetails = (task: TaskDetails, isSms: boolean) => {
        const recipients = isSms ? (task.toMobileNumber ? [task.toMobileNumber] : []) : (task.toEmailAddresses ? splitAddresses(task.toEmailAddresses) : []);
        const copied = !isSms && task.ccEmailAddresses ? splitAddresses(task.ccEmailAddresses) : [];
        const hasFrom = !isSms && !!task.fromEmailAddress;
        if (!hasFrom && recipients.length === 0 && copied.length === 0 && !task.emailTemplateName) {
            return null;
        }
        return (
            <div className={classes.sentDetailsPanel}>
                {hasFrom && (
                    <>
                        <span className={classes.sentDetailsLabel}>From</span>
                        <span className={classes.sentDetailsValue}>{task.fromEmailAddress}</span>
                    </>
                )}
                {recipients.length > 0 && (
                    <>
                        <span className={classes.sentDetailsLabel}>To</span>
                        {isSms ? (
                            <span className={classes.sentDetailsValue}>{recipients[0]}</span>
                        ) : (
                            <span className={clsx(classes.sentDetailsValue, classes.sentDetailsPills)}>
                                {recipients.map((address) => <span key={address} className={classes.sentAddressPill}>{address}</span>)}
                            </span>
                        )}
                    </>
                )}
                {copied.length > 0 && (
                    <>
                        <span className={classes.sentDetailsLabel}>Cc</span>
                        <span className={clsx(classes.sentDetailsValue, classes.sentDetailsPills)}>
                            {copied.map((address) => <span key={address} className={classes.sentAddressPill}>{address}</span>)}
                        </span>
                    </>
                )}
                {task.emailTemplateName && (
                    <>
                        {(hasFrom || recipients.length > 0 || copied.length > 0) && <hr className={classes.sentDetailsDivider} />}
                        <span className={classes.sentDetailsLabel}>Template</span>
                        <span className={classes.sentDetailsValue}>
                            <span className={clsx(classes.sentTemplatePill, isSms && classes.sentTemplatePillSms)}>
                                <DescriptionOutlinedIcon />
                                <span>{task.emailTemplateName}</span>
                            </span>
                        </span>
                    </>
                )}
            </div>
        );
    };

    const getDetailContent = (task: TaskDetails) => {

        // Explicit request: see isCreateTaskCancelledByReOpen - say what happened instead of showing the usual create body.
        if (isCreateTaskCancelledByReOpen(task)) {
            return (
                <Paper elevation={3} className={classes.detailsContainer}>
                    <p className={classes.notes}>Cancelled when the enquiry was re-opened.</p>
                </Paper>
            );
        }

        // Explicit request: a follow-up that is still pending and carries nothing else (what Re-Open leaves behind)
        // was an empty card. It now says what is expected of the user.
        if (task.taskTypeDescription.toLowerCase() === 'enquiry follow up'
            && !task.completedDate
            && !task.followUpNotes
            && !task.followUpAction
            && !task.outcome
            && task.taskTypeInternalDescription !== 'BookMeeting'
            && task.taskTypeInternalDescription !== 'MeetingTask') {
            return (
                <Paper elevation={3} className={classes.detailsContainer}>
                    <p className="description">
                        This follow-up is due. <span>Follow up to continue with this enquiry.</span>
                    </p>
                </Paper>
            );
        }

        if (task.taskTypeDescription.toLowerCase() === 'assign enquiry' && task.assignedToGuidID) {
            // Explicit request: assignment doesn't need a card - the quiet line
            // (activityContent's quietContent, same "Assigned to" chip) already carries this.
            // A note is real user-authored content though, so that one case keeps the card.
            if (!task.followUpNotes) {
                return undefined;
            }

            return (
                <Paper elevation={3} className={classes.detailsContainer}>
                    <h5 className="title">
                        Assigned to <span className="assigneeChip">{task.assignedToName}</span> by <span>{task.assignedByName}</span>
                    </h5>
                    {task.followUpNotes && (
                        <ExpandableNote
                            html={convertMentionInCommentToHtml(task.followUpNotes)}
                            plainText={stripMentionsToPlainText(task.followUpNotes)}
                        />
                    )}
                </Paper>
            );
        } else if (task.enquiryLinkedMatter || task.taskTypeDescription.toLowerCase() === 'link matter') {
            return (
                <Paper elevation={3} className={classes.detailsContainer}>
                    {task.ownerName && (
                        <h5 className="title authorNameTitle">
                            {task.ownerName}
                        </h5>
                    )}
                    {task.enquiryLinkedMatter
                        ? (
                            <h5 className="title">
                                {`${task.linkedMatterClient ? task.linkedMatterClient : '' } - ${task.linkedMatterTitle } (${task.enquiryLinkedMatter})`}
                            </h5>
                        )
                        : (
                            // Explicit request: the same soft notice row as Auto Follow Up Removed instead of the old yellow note, and no asking the
                            // user to check back.
                            <p className={clsx(classes.autoFollowUpNotice, classes.autoFollowUpNoticeRemoved)}>
                                <AutorenewOutlinedIcon className={classes.autoFollowUpNoticeIcon} />
                                <span className={classes.autoFollowUpNoticeText}>
                                    The matter is created and linked. Its details are in {props.sourceSystem || 'the source system'} and will appear here after the next sync.
                                </span>
                            </p>
                        )
                    }
                </Paper>
            );
        } else if (task.taskTypeInternalDescription === 'BookMeeting') {
            const meetingInfo = getMeetingInfo(task);
            const showMeetingInfo = !!meetingInfo.title;
            const meetingDate = (showMeetingInfo && task.bookMeetingStartDateUTC)
                ? moment(convertToLocalTimeZone(task.bookMeetingStartDateUTC.toString()))
                : undefined;
            const reminderMethodLabel = !task.meetingReminderOffset
                ? undefined
                : (task.isReminderByEmail && task.isReminderBySms)
                    ? 'Email & SMS'
                    : task.isReminderByEmail
                        ? 'Email'
                        : task.isReminderBySms
                            ? 'SMS'
                            : undefined;
            const hasAnyStat = (task.sendConfirmation !== undefined && !task.isAssignedToTeam)
                || !!task.feeApplicable
                || !!reminderMethodLabel
                || !!(task.assignedToName && task.isAssignedToTeam);

            return (
                <Paper elevation={3} className={classes.detailsContainer}>
                    {showMeetingInfo && (
                        <div className={classes.meetingBody}>
                            {meetingDate && (
                                <div className={clsx(classes.meetingDateBadge, isBookMeetingDone(task) && classes.meetingDateBadgeDone)}>
                                    <div className={clsx(classes.meetingDateBadgeMonth, isBookMeetingDone(task) && classes.meetingDateBadgeMonthDone)}>{meetingDate.format('MMM')}</div>
                                    <div className={classes.meetingDateBadgeDay}>{meetingDate.format('D')}</div>
                                </div>
                            )}
                            <div className={classes.meetingInfo}>
                                {meetingInfo.title}
                                {meetingInfo.dateTimeText && (
                                    <span className={classes.meetingDateTimeText}>{meetingInfo.dateTimeText}</span>
                                )}
                                {meetingInfo.locationText && (
                                    <span className={classes.meetingLocationText}>{meetingInfo.locationText}</span>
                                )}
                            </div>
                        </div>
                    )}

                    {hasAnyStat && (
                        <div className={classes.meetingStatsRow}>
                            {task.sendConfirmation !== undefined && !task.isAssignedToTeam && (
                                <div className={classes.meetingStat}>
                                    <span className={classes.meetingStatLabel}>Confirmation</span>
                                    <span className={clsx(classes.meetingStatValue, task.sendConfirmation && classes.meetingStatValuePositive)}>
                                        <span className={clsx(classes.meetingStatDot, task.sendConfirmation && classes.meetingStatDotPositive)} />
                                        {task.sendConfirmation ? 'Sent' : 'Not sent'}
                                    </span>
                                </div>
                            )}
                            {!!task.feeApplicable && (
                                <div className={classes.meetingStat}>
                                    <span className={classes.meetingStatLabel}>Fee</span>
                                    <span className={classes.meetingStatValue}>{props.currency}{formatNumber(task.feeApplicable, 2)}</span>
                                </div>
                            )}
                            {reminderMethodLabel && (
                                <div className={classes.meetingStat}>
                                    <span className={classes.meetingStatLabel}>Reminder</span>
                                    <span className={classes.meetingStatValue}>{reminderMethodLabel} &middot; {task.meetingReminderOffset} before</span>
                                </div>
                            )}
                            {task.assignedToName && task.isAssignedToTeam && (
                                <div className={classes.meetingStat}>
                                    <span className={classes.meetingStatLabel}>Assigned To</span>
                                    <span className={classes.meetingStatValue}>{task.assignedToName}</span>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Explicit request: meeting notes can run long (same complaint followUpNotes
                        already had Read More for) - this is the one rendering spot for
                        task.meetingNotes in the whole file, shared by both a freshly booked
                        meeting and one later marked Had Meeting (same BookMeeting task record,
                        just completedDate/followUpActionSystemName updated), so this single
                        change covers both card states. */}
                    {task.meetingNotes && (
                        <div className={classes.meetingNotesBlock}>
                            <span className={classes.meetingStatLabel}>Meeting Notes</span>
                            <ClampedMeetingNotes text={task.meetingNotes} />
                        </div>
                    )}

                    {task.followUpNotes && (
                        <ExpandableNote
                            html={convertMentionInCommentToHtml(task.followUpNotes, 'Follow Up Notes')}
                            plainText={stripMentionsToPlainText(task.followUpNotes, 'Follow Up Notes')}
                        />
                    )}

                    {task.emailContent && (
                        <Button
                            color="primary"
                            size="small"
                            className={classes.viewEmailLink}
                            endIcon={<OpenInNewIcon style={{ fontSize: 15 }} />}
                            onClick={() => {
                                setState((prevState) => {
                                    return {
                                        ...prevState,
                                        showEmailContent: true,
                                        taskGuid: task.taskGuidID
                                    };
                                });
                            }}
                        >
                            View email content
                        </Button>
                    )}

                    <div className="author">Created by: <span>{task.assignedByName}</span></div>
                </Paper>
            );
        } else if (task.taskTypeInternalDescription === 'MeetingReminder') {
            return (
                <Paper elevation={3} className={classes.detailsContainer}>
                    <p className="description">Meeting reminder sent by <span>{task.isEmailContentExists ? 'Email' : task.isMessageContentExists ? 'SMS' : ''}</span> to <span>{props.enquiryDetail.name  || props.enquiryDetail.firstName + ' ' + props.enquiryDetail.lastName}</span>
                    </p>
                    {(task.isMessageContentExists || task.isEmailContentExists) && (
                        <Button
                            color="primary"
                            size="small"
                            className={classes.viewEmailLink}
                            endIcon={<OpenInNewIcon style={{ fontSize: 15 }} />}
                            onClick={() => {
                                setState((prevState) => {
                                    return {
                                        ...prevState,
                                        showEmailContent: task.isEmailContentExists ? true : false,
                                        showMessageContent: task.isMessageContentExists ? true : false,
                                        taskGuid: task.taskGuidID
                                    };
                                });
                            }}
                        >
                            {`View ${task.isEmailContentExists ? 'email' : 'SMS'} content`}
                        </Button>
                    )}
                    <div className="author">Created by: <span>{task.assignedByName}</span></div>
                </Paper>
            );
        } else if (task.taskTypeInternalDescription === 'MeetingTask' &&
                (task.followUpNotes || task.outcomeNotes)) {
            return (
                <Paper elevation={3} className={classes.detailsContainer}>
                    {task.assignedByName && (
                        <h5 className="title authorNameTitle">
                            {task.assignedByName}
                        </h5>
                    )}
                    {task.followUpNotes && (
                        <ExpandableNote
                            html={convertMentionInCommentToHtml(task.followUpNotes)}
                            plainText={stripMentionsToPlainText(task.followUpNotes)}
                            // the name above is hidden, so the note is the first thing in the card: no extra top margin
                            style={{ marginTop: 0 }}
                        />
                    )}
                    {task.outcomeNotes && (
                        <PreviousNotes notes={task.outcomeNotes} createdDate={task.createdDate} />
                    )}
                </Paper>
            );
        } else if (task.systemAction === 'CreateEmail') {
            // Same "nothing left to show once the name is hidden" guard as the generic
            // branches below - 'Sent by:' is a div.author (always hidden) so it never counts.
            if (!task.isAutoFollowUpSent && task.emailSubject === undefined && !task.fromEmailAddress
                && !task.toEmailAddresses && !task.ccEmailAddresses && !task.emailTemplateName
                && !task.emailContent && !task.followUpNotes && !task.outcomeNotes) {
                return undefined;
            }
            return (
                <Paper elevation={3} className={classes.detailsContainer}>
                    <div className={classes.emailFields}>
                        {task.isAutoFollowUpSent && (
                            <p className="emailDescription">Auto Follow Up Sent</p>
                        )}
                        {task.emailSubject !== undefined && (
                            <h5 className={classes.emailSubject}>{task.emailSubject}</h5>
                        )}
                        {renderSentDetails(task, false)}
                        {task.emailContent && (
                            <Button
                                color="primary"
                                size="small"
                                className={classes.viewEmailLink}
                                endIcon={<OpenInNewIcon style={{ fontSize: 15 }} />}
                                onClick={() => {
                                    setState((prevState) => {
                                        return {
                                            ...prevState,
                                            showEmailContent: true,
                                            taskGuid: task.taskGuidID
                                        };
                                    });
                                }}
                            >
                                View full email
                            </Button>
                        )}
                        {task.followUpNotes && (
                            <ExpandableNote
                                html={convertMentionInCommentToHtml(task.followUpNotes)}
                                plainText={stripMentionsToPlainText(task.followUpNotes)}
                            />
                        )}
                        <div className="author">Sent by: <span>{task.assignedByName}</span></div>
                        {task.outcomeNotes && (
                            <PreviousNotes notes={task.outcomeNotes} createdDate={task.createdDate} />
                        )}
                    </div>
                </Paper>
            );
        } else if (task.systemAction === 'CreateSMS') {
            // Same guard as CreateEmail above.
            if (!task.toMobileNumber && !task.emailTemplateName && !task.emailContent
                && !task.followUpNotes && !task.outcomeNotes) {
                return undefined;
            }
            return (
                <Paper elevation={3} className={classes.detailsContainer}>
                    <div className={classes.emailFields}>
                        {renderSentDetails(task, true)}
                        {task.emailContent && (
                            <Button
                                color="primary"
                                size="small"
                                className={classes.viewEmailLink}
                                endIcon={<OpenInNewIcon style={{ fontSize: 15 }} />}
                                onClick={() => {
                                    setState((prevState) => {
                                        return {
                                            ...prevState,
                                            showMessageContent: true,
                                            taskGuid: task.taskGuidID
                                        };
                                    });
                                }}
                            >
                                View full message
                            </Button>
                        )}
                        {task.followUpNotes && (
                            <ExpandableNote
                                html={convertMentionInCommentToHtml(task.followUpNotes)}
                                plainText={stripMentionsToPlainText(task.followUpNotes)}
                            />
                        )}
                        <div className="author">Sent by: <span>{task.assignedByName}</span></div>
                        {task.outcomeNotes && (
                            <PreviousNotes notes={task.outcomeNotes} createdDate={task.createdDate} />
                        )}
                    </div>
                </Paper>
            );
        } else if (task.systemAction === 'SendDataForm') {
            // Same guard as CreateEmail above.
            if (task.emailSubject === undefined && !task.fromEmailAddress && !task.toEmailAddresses
                && !task.ccEmailAddresses && !task.emailTemplateName && !task.emailContent
                && !task.followUpNotes && !task.outcomeNotes) {
                return undefined;
            }
            return (
                <Paper elevation={3} className={classes.detailsContainer}>
                    <div className={classes.emailFields}>
                        {task.emailSubject !== undefined && (
                            <h5 className={classes.emailSubject}>{task.emailSubject}</h5>
                        )}
                        {renderSentDetails(task, false)}
                        {task.emailContent && (
                            <Button
                                color="primary"
                                size="small"
                                className={classes.viewEmailLink}
                                endIcon={<OpenInNewIcon style={{ fontSize: 15 }} />}
                                onClick={() => {
                                    setState((prevState) => {
                                        return {
                                            ...prevState,
                                            showEmailContent: true,
                                            taskGuid: task.taskGuidID
                                        };
                                    });
                                }}
                            >
                                View full email
                            </Button>
                        )}
                        {task.followUpNotes && (
                            <ExpandableNote
                                html={convertMentionInCommentToHtml(task.followUpNotes)}
                                plainText={stripMentionsToPlainText(task.followUpNotes)}
                            />
                        )}
                        <div className="author">Sent by: <span>{task.assignedByName}</span></div>
                        {task.outcomeNotes && (
                            <PreviousNotes notes={task.outcomeNotes} createdDate={task.createdDate} />
                        )}
                    </div>
                </Paper>
            );
        } else if ((task.dueDate && task.completedDate) && getDescription(task) !== 'Follow Up Due') {
            // Explicit bug report: with the assignee's name now hidden here (shown in the card
            // header instead - see '.authorNameTitle'), a task with none of these optional
            // fields rendered a Paper with nothing visible in it - a blank bordered box under
            // the header. Same fields this branch already checks individually below, just
            // checked once up front to catch the "none of them" case and skip the card body
            // entirely instead (still a header-only card, per "other cards... still render the
            // header card" - just without an empty box beneath it).
            const hasVisibleContent = !!task.enquiryLinkedMatter || !!task.followUpNotes || !!task.outcomeNotes
                || hasAutoFollowUpContent(task)
                || hasAutoFollowUpRemovedContent(task.isAutoFollowUpRemoved, task.isAutoFollowUpSent);
            if (!hasVisibleContent) {
                return undefined;
            }
            return (
                <Paper elevation={3} className={classes.detailsContainer}>
                    {task.assignedByName && (
                        <h5 className="title authorNameTitle">
                            {task.assignedByName}
                        </h5>
                    )}
                    {task.enquiryLinkedMatter && (
                            <h5 className="title">
                                Filenumber: <span>{task.enquiryLinkedMatter}</span>
                            </h5>
                    )                    }
                    {task.followUpNotes && (
                        <ExpandableNote
                            html={convertMentionInCommentToHtml(task.followUpNotes)}
                            plainText={stripMentionsToPlainText(task.followUpNotes)}
                            // first in the card (the name above is hidden) unless the Filenumber line comes before it; the
                            // note's own 10px top margin otherwise left an extra band under the card header (Internal Note)
                            style={task.enquiryLinkedMatter ? undefined : { marginTop: 0 }}
                        />
                    )}
                    {task.outcomeNotes && (
                        <PreviousNotes notes={task.outcomeNotes} createdDate={task.createdDate} />
                    )}
                    {getAutFollowUp(task)}
                    {getAutoFollowUpRemoved(task.isAutoFollowUpRemoved, task.isAutoFollowUpSent)} 
                </Paper>
            );
        } else if (task.followUpNotes) {
            return (
                <Paper elevation={3} className={classes.detailsContainer}>
                    {task.assignedByName && (
                        <h5 className="title authorNameTitle">
                            {task.assignedByName}
                        </h5>
                    )}
                    <ExpandableNote
                        html={convertMentionInCommentToHtml(task.followUpNotes)}
                        plainText={stripMentionsToPlainText(task.followUpNotes)}
                        style={{ marginTop: 0 }}
                    />
                    {getAutFollowUp(task)}
                    {getAutoFollowUpRemoved(task.isAutoFollowUpRemoved, task.isAutoFollowUpSent)}
                </Paper>
            );
        } else if (getDescription(task) === 'Follow Up Due' && task.outcomeNotes) {
            return (
                <Paper elevation={3} className={classes.detailsContainer}>
                    {task.assignedByName && (
                        <h5 className="title authorNameTitle">
                            {task.assignedByName}
                        </h5>
                    )}
                    {task.outcomeNotes && (
                        <p className="description">{task.outcomeNotes}</p>
                    )}
                                                       
                </Paper>
            );
        } else if (task.assignedByName && task.completedDate) {
            // Same "nothing left to show once the name is hidden" case as the branch above -
            // this one's only other possible content is the Auto Follow Up Removed message.
            if (!hasAutoFollowUpRemovedContent(task.isAutoFollowUpRemoved, task.isAutoFollowUpSent)) {
                return undefined;
            }
            return (
                <Paper elevation={3} className={classes.detailsContainer}>
                    {task.assignedByName && (
                        <h5 className="title authorNameTitle">
                            {task.assignedByName}
                        </h5>
                    )}
                    {getAutoFollowUpRemoved(task.isAutoFollowUpRemoved, task.isAutoFollowUpSent)}
                </Paper>
            );
        } else if (task.taskUpdateInProgress && task.isIntegrationFailed === false) {
            return (
                <Paper elevation={3} className={classes.detailsContainer}>
                    {task.assignedByName && (
                        <h5 className="due-date">
                            In Progress
                        </h5>
                    )}
                </Paper>
            );
        } else if (task.isExternalIntegrationInProgress) {
            return (
                // For ActionStep, Smokeball and Affinity integration progress
                <Paper elevation={3} className={classes.detailsContainer}>
                    {task.assignedByName && (
                        <>
                            {task.ownerName && (
                                <h5 className="title authorNameTitle">
                                    {task.ownerName}
                                </h5>
                            )}
                            <p className={classes.notes}>
                                The Create Matter request has been submitted.
                                The Integration is in progress. Please check back later for updates.
                            </p>
                        </>
                    )}
                </Paper>
            );
        } else if (task.isIntegrationFailed) {
            // Explicit request: the "didn't finish" card (see isFailedIntegrationTask) - the checklist is built as data here
            // (same rows, same per-CRM conditions and same "is it done" tests as the ListItem rows this replaced) so the card
            // can also count the completed steps and show a status word on each one.
            const createTargetName = props.canCreateMatter ? 'Matter' : 'Client';
            const failedSteps: { label: string; isDone: boolean }[] = [
                { label: 'Contact Created / Updated', isDone: !!props.enquiryDetail.integrationContactId },
                { label: 'Client Created / Updated', isDone: !!props.enquiryDetail.integrationClientId },
            ];
            if (props.enquiryDetail.otherPartyFirstName && props.enquiryDetail.otherPartyLastName) {
                failedSteps.push({ label: 'Other Party Created', isDone: !!props.enquiryDetail.integrationOtherPartyId });
            }
            if (props.canCreateMatter) {
                if (props.sourceSystem.toLowerCase() === 'leap') {
                    failedSteps.push(
                        { label: 'Matter Created', isDone: !!props.enquiryDetail.integrationMatterId },
                        { label: 'Matter Document Created', isDone: !!props.enquiryDetail.integrationMatterDocumentId },
                    );
                }
                if (props.sourceSystem.toLowerCase() === 'actionstep' || props.sourceSystem.toLowerCase() === 'smokeball' || props.sourceSystem.toLowerCase() === 'affinity') {
                    failedSteps.push({ label: 'Matter Created', isDone: !!(props.enquiryDetail.integrationMatterId || props.enquiryDetail.tempIntegrationMatterId) });
                }
            }
            if (props.sourceSystem.toLowerCase() === 'actionstep') {
                failedSteps.push({
                    label: 'Matter Relationship Created',
                    // If the matter relationship failed, we will only have the tempIntegrationMatterId
                    isDone: !(props.enquiryDetail.tempIntegrationMatterId && props.enquiryDetail.integrationMatterId == null),
                });
            }
            const completedStepCount = failedSteps.filter((failedStep) => failedStep.isDone).length;

            return (
                <Paper elevation={3} className={classes.detailsContainer}>
                    {task.assignedByName && (
                        <>
                            <div className={classes.failedSummary}>
                                <p className={classes.failedSummaryText}>The integration stopped partway.</p>
                                <span className={classes.failedProgress} aria-hidden={true}>
                                    {failedSteps.map((failedStep) => (
                                        <i
                                            key={failedStep.label}
                                            className={clsx(classes.failedProgressSegment, failedStep.isDone && classes.failedProgressSegmentDone)}
                                        />
                                    ))}
                                </span>
                                <span className={classes.failedProgressCount}>
                                    {completedStepCount} of {failedSteps.length} steps completed
                                </span>
                            </div>
                            <ul className={classes.failedStepList}>
                                {failedSteps.map((failedStep) => (
                                    <li
                                        key={failedStep.label}
                                        className={clsx(classes.failedStep, failedStep.isDone ? classes.failedStepDone : classes.failedStepTodo)}
                                    >
                                        {failedStep.isDone
                                            ? <CheckCircleIcon className={classes.failedStepIcon} />
                                            : <RemoveCircleOutlinedIcon className={classes.failedStepIcon} />
                                        }
                                        <span className={classes.failedStepName}>{failedStep.label}</span>
                                        <span className={classes.failedStepStatus}>{failedStep.isDone ? 'Created' : 'Not created'}</span>
                                    </li>
                                ))}
                            </ul>
                            <div className={classes.failedGuide}>
                                <p>To try again, select the Create {createTargetName} button.</p>
                                <p>Already created the {createTargetName.toLowerCase()} yourself? Mark it as created to link it to this enquiry.</p>
                            </div>
                            <p className={classes.failedNote}>
                                <InfoOutlinedIcon />
                                Steps marked Created were completed before the problem occurred.
                            </p>
                        </>
                    )}
                </Paper>
            );
        } else {
            return undefined;
        }
    };

    // Explicit request: a day header row - small hollow dot on the rail (same 32px column as
    // the real nodes) plus the uppercase label, before the first row of each new calendar day.
    const renderDayHeader = (date: Date) => (
        <div className={classes.dayHeaderRow}>
            <div className="connector" />
            <div className={classes.dayHeaderDotWrapper}>
                <div className={classes.dayHeaderDot} />
            </div>
            <div className={classes.dayHeaderLabel}>
                {formatDayHeaderLabel(date)}
            </div>
        </div>
    );

    // Explicit request: assignment reads as a quiet line ("X assigned this Enquiry to [chip Y]")
    // rather than a card - same gate getDetailContent's own assign-enquiry branch uses, so this
    // and that branch's undefined/card decision always agree on the same task.
    const getAssignEnquiryQuietContent = (task: TaskDetails): React.ReactNode => {
        if (task.taskTypeDescription.toLowerCase() !== 'assign enquiry' || !task.assignedToGuidID) {
            return undefined;
        }
        const enquiryText = task.isKeyOpportunity ? t('KeyOpportunity') : t('Enquiry');
        return (
            <>
                {task.assignedByName} assigned this {enquiryText} to <span className={classes.inlineChip}>{task.assignedToName}</span>
            </>
        );
    };

    // Explicit request: the Create Matter/Client task whose integration stopped partway - the same task getDetailContent's
    // own isIntegrationFailed branch renders its checklist for. The extra conditions mirror the earlier getDetailContent
    // branches that would otherwise claim such a task first (follow-up notes; completedDate), so the red "didn't finish"
    // node/label/border only ever appears on a card that actually shows that checklist.
    const isFailedIntegrationTask = (task: TaskDetails): boolean =>
        !!task.isIntegrationFailed && !task.isExternalIntegrationInProgress && !task.followUpNotes && !task.completedDate;

    const activityContent = (task: TaskDetails) => {
        const showDetails  = task.outcome && task.outcome.toLowerCase() === 'not proceeding' ? false : true;
        const typeLabel = getDescription(task);
        // Explicit request: the same dashed-orange "upcoming" card treatment Follow Up Due
        // already gets, extended to a booked meeting that hasn't happened yet - its own
        // scheduled start time (not task.dueDate, which Follow Up Due uses) still being in the
        // future is this task type's equivalent of "due".
        const meetingStartDate = task.taskTypeInternalDescription === 'BookMeeting' && task.bookMeetingStartDateUTC
            ? convertToLocalTimeZone(task.bookMeetingStartDateUTC.toString())
            : null;
        // Explicit request: multiple "Book a Meeting" entries can exist for the same enquiry
        // (e.g. rescheduled) - only the most recently booked one is still a live, orange-worthy
        // "upcoming" meeting; an earlier one that's since been superseded by a newer booking
        // shouldn't colour even if nothing ever explicitly marked it done. Same createdDate
        // ordering getTaskTimestamp's own comment already uses for BookMeeting (when it was
        // booked, not when the meeting itself happens).
        const isLatestBookMeeting = !!meetingStartDate && !props.leadDetailsHistory.tasks.some((otherTask: TaskDetails) => {
            if (otherTask === task || otherTask.taskTypeInternalDescription !== 'BookMeeting') {
                return false;
            }
            const thisCreated = task.createdDate ? new Date(task.createdDate).getTime() : 0;
            const otherCreated = otherTask.createdDate ? new Date(otherTask.createdDate).getTime() : 0;
            return otherCreated > thisCreated;
        });
        // Explicit bug report: this task's own scheduled time being in the future isn't enough -
        // there's no field linking a BookMeeting task back to whichever later "Had Meeting"
        // (followUpActionSystemName === 'HadMeeting') follow-up might have logged that it
        // actually happened, so a HadMeeting entry timestamped on/after this meeting's own
        // start time is taken as "this one's done" - covers the meeting having been held early/
        // on time/logged late, without needing an explicit link between the two task records.
        const hasSubsequentHadMeeting = !!meetingStartDate && props.leadDetailsHistory.tasks.some((otherTask: TaskDetails) => {
            if (otherTask.followUpActionSystemName !== 'HadMeeting') {
                return false;
            }
            const hadMeetingDate = otherTask.completedDate ?? otherTask.createdDate;
            return !!hadMeetingDate && new Date(hadMeetingDate).getTime() >= meetingStartDate.getTime();
        });
        // Explicit request: a booked meeting colours orange only when it's the latest booking,
        // its own scheduled start time is still in the future, and no Had Meeting activity has
        // logged it as done since.
        const isUpcomingMeeting = !!meetingStartDate && isLatestBookMeeting && meetingStartDate.getTime() > Date.now()
            && !hasSubsequentHadMeeting && !isBookMeetingDone(task);
        // Explicit request: a booked meeting uses its own, more specific isUpcomingMeeting rule
        // above (latest booking + still in the future + not yet had) rather than the plain
        // completedDate-null rule - BookMeeting task records don't reliably get a completedDate
        // just because they're no longer the live/latest one. Every other task type keeps the
        // simple "completedDate is null" rule.
        const isUpcoming = task.taskTypeInternalDescription === 'BookMeeting' ? isUpcomingMeeting : !task.completedDate;
        // getTaskTimestamp uses this task's own createdDate for BookMeeting (kept for the
        // timeline's day-grouping/sort order, computed separately in the main render loop) -
        // but the pill shown on an upcoming meeting card needs to count down to when the
        // MEETING happens, not when it was booked, so this one swaps in meetingStartDate.
        const timestamp = isUpcomingMeeting ? meetingStartDate : getTaskTimestamp(task);
        // Explicit bug report/old-implementation behaviour: assignedByName/ownerName is who
        // completed the task - for a due-but-not-yet-done item there's no one to attribute it
        // to yet, so no author shows next to it (same as the old implementation). Scoped to
        // Follow Up Due specifically, not isUpcomingMeeting - a booked meeting's assignedByName
        // is who created/booked it (see getMeetingInfo's own "Created by" line), which is
        // already meaningful before the meeting happens, unlike a follow-up's "who completed it".
        // Explicit request: a pending assignment / create task is due, like a Follow Up Due - the name on it was the user who created that pending
        // task, which read as if they had done it. So it shows no author, and the label says it is due.
        const isPendingTaskCard = isPendingSystemTask(task);
        const cancellingReOpen = findReOpenThatCancelledCreateTask(task);
        const isCancelledCreateTask = !!cancellingReOpen;
        // The name on a cancelled create task is whoever owned the task, not who cancelled it - that is whoever re-opened the enquiry.
        const author = (typeLabel === 'Follow Up Due' || isPendingTaskCard)
            ? undefined
            : getTaskAuthorName(cancellingReOpen ?? task);
        const isFailed = isFailedIntegrationTask(task);
        // A failed integration task has no completedDate/dueDate to take a time from, so its card showed no time at all -
        // fall back to when the task was created.
        const displayTimestamp = timestamp ?? (isFailed ? task.createdDate ?? null : null);

        return (
            <>
                {props.sortDirection === 'DESC' && task.outcome && task.outcome.toLowerCase() === 'not proceeding' && notProceeding(task)}

                {showDetails === true && (
                    <TimelineRow
                        icon={isFailed
                            ? renderHistoryIcon(<ReportProblemOutlinedIcon fontSize="small" />, 'var(--rv-accent-red-bg)', 'var(--rv-accent-red)')
                            : getHistoryAvatar(task)}
                        typeLabel={isFailed ? `${typeLabel} didn't finish` : isPendingTaskCard ? `${typeLabel} Due` : isCancelledCreateTask ? `${typeLabel} cancelled` : (task.completedDate ? getFinishedCreateLabel(typeLabel) : typeLabel)}
                        author={author}
                        initials={getInitials(author)}
                        timeDisplay={getTimeDisplay(displayTimestamp)}
                        timeTooltip={getTimeTooltip(displayTimestamp)}
                        isUpcoming={isUpcoming && !isFailed}
                        isFailed={isFailed}
                        body={getDetailContent(task)}
                        quietContent={getAssignEnquiryQuietContent(task)}
                        headerAction={(task.taskTypeInternalDescription === 'BookMeeting' && task.canAssignMeeting) ? (
                            <Button
                                variant="outlined"
                                size="small"
                                color="primary"
                                className={classes.headerEditButton}
                                onClick={onAssign(task)}
                            >
                                Edit
                            </Button>
                        ) : undefined}
                    />
                )}

                {props.sortDirection === 'ASC' && task.outcome && task.outcome.toLowerCase() === 'not proceeding' && notProceeding(task)}
            </>
        );
    };

    const enquiryCreated = () => {
        // Same per-tenant Enquiry/Key Opportunity term swap getDescription() already uses -
        // props.enquiryDetail.isKeyOpportunity is this enquiry's own flag (vs. a task's).
        const enquiryText = props.enquiryDetail.isKeyOpportunity ? t('KeyOpportunity') : t('Enquiry');
        const author = props.enquiryDetail.createdByName;
        const timestamp = props.enquiryDetail?.createdDate ?? null;

        return (
            <TimelineRow
                icon={renderHistoryIcon(<CheckOutlinedIcon fontSize="small" />, 'var(--rv-accent-green-bg)', 'var(--rv-accent-green)')}
                typeLabel={`${enquiryText} Created`}
                author={author}
                initials={getInitials(author)}
                timeDisplay={getTimeDisplay(timestamp)}
                timeTooltip={getTimeTooltip(timestamp)}
                body={(
                    <Paper elevation={3} className={classes.detailsContainer}>
                        {props.enquiryDetail.areaOfLaw && (
                            <div className={classes.enquiryCreatedMeta}>
                                <span className={classes.enquiryCreatedMetaItem}>
                                    <GavelIcon className={classes.enquiryCreatedMetaIconArea} />
                                    <span className={classes.enquiryCreatedMetaLabel}>Area of law</span>
                                    <span className={classes.enquiryCreatedMetaValue}>{props.enquiryDetail.areaOfLaw}</span>
                                </span>
                                {props.enquiryDetail.source && props.enquiryDetail.source.trim() && (
                                    <span className={classes.enquiryCreatedMetaItem}>
                                        <MailOutlinedIcon className={classes.enquiryCreatedMetaIconSource} />
                                        <span className={classes.enquiryCreatedMetaLabel}>Source</span>
                                        <span className={classes.enquiryCreatedMetaValue}>{props.enquiryDetail.source.trim()}</span>
                                    </span>
                                )}
                            </div>
                        )}
                        {props.enquiryDetail.natureOfEnquiry && (
                            <ExpandableNote
                                html={convertMentionInCommentToHtml(props.enquiryDetail.natureOfEnquiry)}
                                plainText={stripMentionsToPlainText(props.enquiryDetail.natureOfEnquiry)}
                            />
                        )}
                        {/* TODO: Show Created by as well. We don't have CreatedBy Value yet */}
                        <div className="author">Created by: <span>{props.enquiryDetail.createdByName}</span></div>
                    </Paper>
                )}
            />
        );
    };

    const notProceeding = (task: TaskDetails) => {
        const author = getTaskAuthorName(task);
        const timestamp = task.updatedDate ?? null;
        // Same "name now hidden, is there anything else?" check as getDetailContent's own
        // dueDate/completedDate branches above - avoids an empty bordered box when a "Not
        // Proceeding" outcome was recorded with no notes/reason/referral at all.
        const hasVisibleContent = !!task.followUpNotes || !!task.reason || !!task.referredToFirmName || !!task.notProceedingNotes;

        return (
            <TimelineRow
                icon={renderHistoryIcon(<NotInterestedOutlinedIcon fontSize="small" />, 'var(--rv-accent-grey-bg)', 'var(--rv-accent-grey)')}
                typeLabel="Not Proceeding"
                author={author}
                initials={getInitials(author)}
                timeDisplay={getTimeDisplay(timestamp)}
                timeTooltip={getTimeTooltip(timestamp)}
                body={!hasVisibleContent ? undefined : (
                    <Paper elevation={3} className={classes.detailsContainer}>
                        {task.assignedByName && (
                            <h5 className="title authorNameTitle">{task.assignedByName}</h5>
                        )}
                        {task.reason && (
                            <LabelledBlock icon={<NotInterestedOutlinedIcon />} heading="Reason">{task.reason}</LabelledBlock>
                        )}
                        {task.referredToFirmName && (
                            <LabelledBlock icon={<SupervisorAccountOutlinedIcon />} heading="Referred to">{task.referredToFirmName}</LabelledBlock>
                        )}
                        {task.followUpNotes && (
                            <ExpandableNote
                                html={convertMentionInCommentToHtml(task.followUpNotes)}
                                plainText={stripMentionsToPlainText(task.followUpNotes)}
                            />
                        )}
                        {task.notProceedingNotes && (
                            // Explicit request: a long Not Proceeding note (a full call attendance note) gets the same Read more as the follow-up notes. Escaped first
                            // because ExpandableNote renders HTML and this was plain text before.
                            <ExpandableNote
                                html={task.notProceedingNotes.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')}
                                plainText={task.notProceedingNotes}
                            />
                        )}
                    </Paper>
                )}
            />
        );
    };

    const taskList = JSON.parse(JSON.stringify(props.leadDetailsHistory.tasks));

    const taskCreateClient = taskList.find((task: TaskDetails) => {
        return (task.taskTypeDescription.toLowerCase() === 'create client' || task.taskTypeDescription.toLowerCase() === 'create matter') 
            && task.taskStatusDescription.toLowerCase() === 'not started';
    });

    let newTasks = taskList;

    if (taskCreateClient) {
        const index = taskList.indexOf(taskCreateClient);
        newTasks.splice(index, 1);
    }

    // Client-side only - leadDetailsHistory is already fully loaded by the time this renders.
    // enquiryCreated() (rendered separately below, not part of taskList) only belongs under
    // "All" - explicit request that it not appear under any single category chip.
    const visibleTasks = activeCategory === 'all'
        ? newTasks
        : newTasks.filter((task: TaskDetails) => getActivityCategory(task) === activeCategory);

    const isTaskCreateClientVisible = !!taskCreateClient
        && (activeCategory === 'all' || getActivityCategory(taskCreateClient) === activeCategory);

    const onCloseEmailContent = () => {
        setState((prevState) => {
            return {
                ...prevState,
                showEmailContent: false,
                showMessageContent: false,
                taskGuid: undefined
            };
        });
    };

    // tslint:disable-next-line: no-any
    // const convertToTimeZoneByOffSet = (date: Date | null, offset: any) => {
    //     const momentDate = moment(date);
    //     let uiDate = null;
    //     if (momentDate.isValid() && date) {
    //         uiDate = momentDate.utc().add(offset, 'minutes').toDate();
    //     }
    //     return uiDate;
    // };   
    
    const convertToLocalTimeZone = (date: string) => {
        var oldDate = new Date(date);
        var newDate = new Date(date);
        newDate.setMinutes(oldDate.getMinutes() - oldDate.getTimezoneOffset());
        // console.log('newDate', newDate);
        return newDate;
    }; 

    // if (state.isLoading) {
    //     return (
    //         <div className={`${classes.loadingRoot} ${props.className}`}>
    //             <RvLoader />
    //         </div>
    //     );
    // }

    return (
        <div className={`${classes.root} ${props.className}`}>
            {IS_RENDER_COUNT_ENABLED && <RenderCountBadge label="HistoryActivity" />}
            {
                ((state.showEmailContent || state.showMessageContent) && state.taskGuid) && (
                    <EmailContentDialog
                        onCloseEmailContent={onCloseEmailContent}
                        taskGuid={state.taskGuid}
                        showMessageContent={state.showMessageContent}
                        showEmailContent={state.showEmailContent}
                        
                    />
                    // <Dialog
                    //     open={state.showEmailContent}
                    //     onClose={onCloseEmailContent}
                    //     maxWidth="md"
                    //     fullWidth={true}
                    //     className={classes.dialogRoot}
                    //     scroll={'paper'}
                    //     classes={{
                    //         paper: classes.dialogPaper
                    //     }}                        
                    // >
                    //     <DialogTitle className={classes.header}>
                    //         <span>Email Content</span>
                    //         <Button onClick={onCloseEmailContent} color="primary">
                    //             Close
                    //         </Button>
                    //     </DialogTitle>
                    //     <DialogContent dividers={true}>
                    //         <>
                    //             <Typography variant="h6">{'Attachments'}</Typography> 
                    //             {getFileItems(state.files)}
                    //             <Divider className={classes.divider} />
                    //             <div
                    //                 dangerouslySetInnerHTML={{
                    //                     __html: state.emailContent
                    //                 }}
                    //             />
                    //         </>                                                        
                    //     </DialogContent>                        
                    // </Dialog>
                )
            }
            <div className={classes.filterRow}>
                <div className={classes.filterRowContent} data-tour="enquiry-history-filter">
                    <LightTooltip
                        arrow={true}
                        title={
                            <>
                                {ACTIVITY_FILTER_OPTIONS.flatMap((option) => option.includes).map((value) => (
                                    <div key={value}>{value}</div>
                                ))}
                            </>
                        }
                    >
                        <Chip
                            size="small"
                            label="All"
                            clickable={true}
                            color={activeCategory === 'all' ? 'primary' : 'default'}
                            variant={activeCategory === 'all' ? 'default' : 'outlined'}
                            className={classes.filterChip}
                            onClick={() => setActiveCategory('all')}
                        />
                    </LightTooltip>
                    {ACTIVITY_FILTER_OPTIONS.map((option) => (
                        <LightTooltip
                            key={option.value}
                            arrow={true}
                            title={
                                <>
                                    {option.includes.map((value) => (
                                        <div key={value}>{value}</div>
                                    ))}
                                </>
                            }
                        >
                            <Chip
                                size="small"
                                label={option.label}
                                clickable={true}
                                color={activeCategory === option.value ? 'primary' : 'default'}
                                variant={activeCategory === option.value ? 'default' : 'outlined'}
                                className={classes.filterChip}
                                onClick={() => setActiveCategory(option.value)}
                            />
                        </LightTooltip>
                    ))}
                </div>
            </div>

            {/* Explicit request: day headers between rows whose dates fall on different
                calendar days. Built as one ordered array, in exactly the same order/
                conditions the old flat render used (sortDirection placement of
                enquiryCreated, activeCategory==='all' gating, visibleTasks filter/order,
                isTaskCreateClientVisible gating), so grouping is purely a render-time
                insertion - no change to which items appear or their order. AnimatedItem's
                stagger is kept exactly as before: only visibleTasks rows get it, with the
                same index*30 delay; enquiryCreated/taskCreateClient are untouched by it,
                same as the old render. */}
            {(() => {
                const timelineEntries: Array<{ key: string; date: Date | null; render: () => React.ReactNode; animationDelay?: number }> = [];

                if (props.sortDirection === 'ASC' && activeCategory === 'all') {
                    timelineEntries.push({ key: 'enquiry-created', date: props.enquiryDetail?.createdDate ?? null, render: enquiryCreated });
                }

                visibleTasks.forEach((task: TaskDetails, index: number) => {
                    timelineEntries.push({
                        key: task.taskGuidID || String(index),
                        date: getTaskTimestamp(task),
                        render: () => activityContent(task),
                        animationDelay: index * 30,
                    });
                });

                if (isTaskCreateClientVisible) {
                    timelineEntries.push({ key: 'task-create-client', date: getTaskTimestamp(taskCreateClient), render: () => activityContent(taskCreateClient) });
                }

                if (props.sortDirection === 'DESC' && activeCategory === 'all') {
                    timelineEntries.push({ key: 'enquiry-created', date: props.enquiryDetail?.createdDate ?? null, render: enquiryCreated });
                }

                let lastRenderedDay: moment.Moment | null = null;

                return timelineEntries.map((entry) => {
                    const entryDay = entry.date ? moment(entry.date) : null;
                    const isNewDay = !!entryDay && (!lastRenderedDay || !entryDay.isSame(lastRenderedDay, 'day'));
                    if (entryDay) {
                        lastRenderedDay = entryDay;
                    }

                    return (
                        <React.Fragment key={entry.key}>
                            {isNewDay && renderDayHeader(entry.date as Date)}
                            {entry.animationDelay !== undefined ? (
                                <AnimatedItem noFill delay={entry.animationDelay}>
                                    {entry.render()}
                                </AnimatedItem>
                            ) : (
                                entry.render()
                            )}
                        </React.Fragment>
                    );
                });
            })()}
            <div ref={messagesEndRef} />
        </div>
    );
});
